import React, { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from 'react';
import { GpxRoute, CampingSpot, UserReview, ChargingStation, WikiSpot, ElevationMarker, BathingPlace, SvvCamera, Coordinates } from '../../types';
import {
  MapPin, Activity, Clock, ArrowLeft, ChevronRight, ChevronLeft, ChevronDown, ChevronUp,
  Tent, Coffee, Mountain, Landmark, Loader2, Star, User, Zap, Bird, Phone, Globe, Navigation,
  ExternalLink, Image as ImageIcon, Layers, Map as MapIcon, Plug, Calendar, MapPinned,
  Cloud, CloudFog, CloudLightning, CloudRain, CloudSnow, CloudSun, Sun, Sunrise, Sunset, X, Eye, Orbit, Waves, Thermometer
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ElevationProfile from '../routes/ElevationProfile';
import { MapContainer, TileLayer, Polyline, useMap, Marker, Popup, ZoomControl } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

import VehicleBadge from '../common/VehicleBadge';
import SpotCard from '../spots/SpotCard';
import { createCustomIcon as createSpotIcon, createCustomMarker, chargerIcon, wikiIcon } from '../map/MapUtils';
import { fetchGpxWithProxy, subscribeToRouteReviews, app as firebaseApp, getCachedSpotsInArea, translateRouteText } from '../../services/firebaseService';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { fetchBatchElevations } from '../../services/elevationService';
import { fetchSnowDepth, SnowDepthResult } from '../../services/snowService';
import { MAP_LAYERS } from '../../constants';
import * as SunCalc from 'suncalc';
import { fetchAllOverpassPOIs } from '../../services/overpassService';
import { fetchNVDBData, NVDBObject } from '../../services/nvdbService';
import { fetchChargersInBounds } from '../../services/nobilService';
import { fetchWikiForPath, fetchWikiTranslations } from '../../services/wikiService';
import { isPointNearPolyline, getDistanceAlongRoute, getDistance } from '../../services/geometryUtils';
import { fetchWildlifeForRoute, WildlifeObs, SPECIES_EMOJI } from '../../services/wildlifeService';
import { getWaypointMeta } from '../../services/waypointService';
import { fetchAllNorwegianBathingPlaces, fetchSwedishBathingPlaces } from '../../services/bathingService';
import { fetchCamerasNearRoute, fetchCamerasNearSpot } from '../../services/svvCameraService';

import { BRAND } from '../../constants/mapColors';
const Route3DFlyover = lazy(() => import('../routes/Route3DFlyover'));

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

type MediaSlide =
  | { type: 'image'; url: string }
  | { type: 'attraction-image'; url: string; link: string; title: string }
  | { type: 'webcam'; url: string; name: string; roadName?: string };

interface WeatherDay {
  key: string;
  label: string;
  maxTemp: number;
  minTemp: number;
  symbol: string;
}

// --- Helper components ---

const MapSizeFixer: React.FC = () => {
  const map = useMap();
  useEffect(() => {
    const fix = () => {
      if (!map) return;
      map.invalidateSize({ animate: false, pan: false });
      const c = map.getCenter();
      const z = map.getZoom();
      if (c && z != null) map.setView(c, z, { animate: false });
    };
    map.whenReady(() => {
      setTimeout(fix, 50);
      setTimeout(fix, 300);
      setTimeout(fix, 800);
    });
    let ro: ResizeObserver | null = null;
    const container = map.getContainer();
    if (typeof ResizeObserver !== 'undefined' && container) {
      ro = new ResizeObserver(() => requestAnimationFrame(fix));
      ro.observe(container);
    }
    window.addEventListener('resize', fix);
    return () => {
      window.removeEventListener('resize', fix);
      if (ro) ro.disconnect();
    };
  }, [map]);
  return null;
};

const FitBounds: React.FC<{ coords: [number, number][] }> = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length < 2) return;
    const timer = setTimeout(() => {
      map.invalidateSize();
      const bounds = L.latLngBounds(coords);
      map.fitBounds(bounds, { padding: [42, 42], maxZoom: 15, animate: true });
    }, 600);
    return () => clearTimeout(timer);
  }, [map, coords]);
  return null;
};

const StatCard: React.FC<{ icon: React.ReactNode; count: number; label: string; color: string }> = ({ icon, count, label, color }) => {
  const colorMap: Record<string, string> = {
    emerald: 'bg-emerald-900/20 border-emerald-500/20 text-emerald-500',
    blue: 'bg-blue-900/20 border-blue-500/20 text-blue-500',
    indigo: 'bg-indigo-900/20 border-indigo-500/20 text-indigo-500',
    violet: 'bg-violet-900/20 border-violet-500/20 text-violet-500',
    yellow: 'bg-yellow-900/20 border-yellow-500/20 text-yellow-500',
  };
  return (
    <div className={`p-3 rounded-2xl border ${colorMap[color] || colorMap.emerald} flex flex-col items-center gap-1`}>
      {icon}
      <span className="text-lg font-black text-white leading-none">{count}</span>
      <span className="text-4xs font-black uppercase tracking-tight opacity-70">{label}</span>
    </div>
  );
};

const WeatherSymbolIcon: React.FC<{ symbol: string; size?: number }> = ({ symbol, size = 16 }) => {
  const s = (symbol || '').toLowerCase();
  if (s.includes('clearsky') || s.includes('fair')) return <Sun size={size} className="text-amber-300" />;
  if (s.includes('partlycloudy')) return <CloudSun size={size} className="text-amber-200" />;
  if (s.includes('thunder')) return <CloudLightning size={size} className="text-yellow-300" />;
  if (s.includes('snow') || s.includes('sleet')) return <CloudSnow size={size} className="text-sky-200" />;
  if (s.includes('rain')) return <CloudRain size={size} className="text-sky-300" />;
  if (s.includes('fog')) return <CloudFog size={size} className="text-[var(--text-secondary)]" />;
  return <Cloud size={size} className="text-[var(--text-primary)]" />;
};

function getUiLocale(): string {
  const raw = (window.localStorage.getItem('i18nextLng') || 'no').toLowerCase();
  if (raw.startsWith('en')) return 'en-US';
  return 'no-NO';
}

function normalizeTimestampMs(value: any): number {
  if (!value) return NaN;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.seconds === 'number') return value.seconds * 1000 + Math.floor((value?.nanoseconds || 0) / 1_000_000);
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : NaN;
}

function normalizeWikiSpots(spots: WikiSpot[] | null | undefined): WikiSpot[] {
  if (!Array.isArray(spots) || spots.length === 0) return [];
  const seen = new Set<number>();
  const normalized: WikiSpot[] = [];
  for (const spot of spots) {
    if (!spot || !Number.isFinite(spot.pageid)) continue;
    if (!Number.isFinite(spot.location?.latitude) || !Number.isFinite(spot.location?.longitude)) continue;
    if (seen.has(spot.pageid)) continue;
    seen.add(spot.pageid);
    normalized.push(spot);
  }
  return normalized;
}

// --- Main Component ---

interface SharedRoutePageProps {
  routeId: string;
}

const SharedRoutePage: React.FC<SharedRoutePageProps> = ({ routeId }) => {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || 'no';
  const appLang = useMemo(() => {
    const raw = currentLanguage.toLowerCase();
    if (raw.startsWith('en')) return 'en';
    if (raw.startsWith('de')) return 'de';
    return 'no';
  }, [currentLanguage]);
  const withLang = useCallback((path: string) => {
    const url = new URL(path, window.location.origin);
    url.searchParams.set('lang', appLang);
    return `${url.pathname}${url.search}`;
  }, [appLang]);
  const fricampWebUrl = useMemo(() => {
    const url = new URL('https://www.fricamp.com/');
    url.searchParams.set('lang', appLang);
    return url.toString();
  }, [appLang]);
  const LANG_CYCLE = ['no', 'en', 'de'] as const;
  const toggleLanguage = () => {
    const currentIdx = LANG_CYCLE.findIndex(l => currentLanguage.startsWith(l));
    const nextIdx = (currentIdx + 1) % LANG_CYCLE.length;
    i18n.changeLanguage(LANG_CYCLE[nextIdx]);
  };

  // Auth state — premium-lag (webcams, wildlife, bathing, chargers, NVDB)
  // krever innlogging. Firestore-regler stenger uansett backend, men vi
  // skjuler kallene i UI for å unngå støy og spare nettverk.
  const [isAuthed, setIsAuthed] = useState<boolean>(() => !!getAuth(firebaseApp).currentUser);
  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(firebaseApp), (u) => setIsAuthed(!!u));
    return () => unsub();
  }, []);

  // Core data
  const [route, setRoute] = useState<GpxRoute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [coordinates, setCoordinates] = useState<[number, number][]>([]);

  // Facility data (fetched from external APIs)
  const [wildSpots, setWildSpots] = useState<CampingSpot[]>([]);
  const [campsites, setCampsites] = useState<CampingSpot[]>([]);
  const [restAreas, setRestAreas] = useState<NVDBObject[]>([]);
  const [chargers, setChargers] = useState<ChargingStation[]>([]);
  const [fetchedWikiSpots, setFetchedWikiSpots] = useState<WikiSpot[]>([]);
  const [wildlife, setWildlife] = useState<WildlifeObs[]>([]);
  const [amenities, setAmenities] = useState<CampingSpot[]>([]);
  const [bathingPlaces, setBathingPlaces] = useState<BathingPlace[]>([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [deferredLoading, setDeferredLoading] = useState<Set<string>>(new Set());
  const loadedTabsRef = useRef<Set<string>>(new Set());

  // UI state
  // showLegal removed — not needed on public sharing pages
  const [mapType, setMapType] = useState<'topo' | 'satellite' | 'dark'>('topo');
  const [showFlyover, setShowFlyover] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const swipeStartXRef = useRef<number | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [activeInfoTab, setActiveInfoTab] = useState<'camping' | 'external_camping' | 'rest' | 'chargers' | 'attractions' | 'wildlife' | 'amenities' | 'bathing'>('camping');
  const [tabPage, setTabPage] = useState<Record<string, number>>({});

  // Weather
  const [weather7d, setWeather7d] = useState<WeatherDay[]>([]);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [sunData, setSunData] = useState<{ sunrise: string; sunset: string; daylightHours: number } | null>(null);
  const [snowData, setSnowData] = useState<SnowDepthResult | null>(null);

  // Reviews
  const [reviews, setReviews] = useState<UserReview[]>([]);

  // Webcams
  const [webcams, setWebcams] = useState<SvvCamera[]>([]);
  const [failedWebcamIds, setFailedWebcamIds] = useState<Set<string>>(new Set());

  // Tesla logic
  const [teslaQrData, setTeslaQrData] = useState<{ lat: number; lon: number; name?: string; spotId?: string } | null>(null);

  // Translation
  const [translatedName, setTranslatedName] = useState<string | null>(null);
  const [translatedDescription, setTranslatedDescription] = useState<string | null>(null);
  const [translating, setTranslating] = useState(false);

  // Content translation (wiki attractions)
  const [translatedWikiSpots, setTranslatedWikiSpots] = useState<Map<number, WikiSpot>>(new Map());
  const [contentTranslating, setContentTranslating] = useState(false);
  const [contentTranslated, setContentTranslated] = useState(false);

  // --- Translate route name/description when language is not Norwegian ---
  useEffect(() => {
    if (!route) return;
    const isNorwegian = currentLanguage.startsWith('no');

    // Reset translations when switching back to Norwegian
    if (isNorwegian) {
      setTranslatedName(null);
      setTranslatedDescription(null);
      return;
    }

    // Check if we already have a cached translation in the route doc
    const lang = currentLanguage.startsWith('de') ? 'de' : 'en';
    const cached = route.translations?.[lang];
    if (cached?.name && cached?.description) {
      setTranslatedName(cached.name);
      setTranslatedDescription(cached.description);
      return;
    }

    // Fetch translation from Cloud Function
    let cancelled = false;
    setTranslating(true);
    translateRouteText(route.id, lang)
      .then((res) => {
        if (cancelled) return;
        if (res.ok && res.data) {
          setTranslatedName(res.data.name);
          setTranslatedDescription(res.data.description);
        }
      })
      .catch(() => { /* keep original text on failure */ })
      .finally(() => { if (!cancelled) setTranslating(false); });

    return () => { cancelled = true; };
  }, [route, currentLanguage]);

  // Reset wiki translations when switching back to Norwegian
  useEffect(() => {
    if (currentLanguage.startsWith('no')) {
      setContentTranslated(false);
      setTranslatedWikiSpots(new Map());
    }
  }, [currentLanguage]);

  const handleTranslateContent = async () => {
    if (contentTranslating || fetchedWikiSpots.length === 0) return;
    setContentTranslating(true);
    const lang = currentLanguage.startsWith('de') ? 'de' : 'en';
    try {
      const translated = await fetchWikiTranslations(fetchedWikiSpots, lang as 'en' | 'de');
      setTranslatedWikiSpots(translated);
      setContentTranslated(true);
    } catch {
      /* keep original text on failure */
    } finally {
      setContentTranslating(false);
    }
  };

  const getEffectiveWiki = (spot: WikiSpot) => translatedWikiSpots.get(spot.pageid) || spot;
  const persistedWikiSpots = useMemo(() => normalizeWikiSpots(route?.wikiSpots), [route?.wikiSpots]);

  // Resolved display text (translated or original)
  const displayName = translatedName || route?.name || '';
  const displayDescription = translatedDescription || route?.description || '';

  // --- Dynamic document.title ---
  useEffect(() => {
    const previousTitle = document.title;
    if (route) {
      const region = route.region ? t(`regions.${route.region}`) : '';
      document.title = `${displayName}${region ? ` | ${region}` : ''} - Fricamp ${t('shared.route_suggestion')}`;
    }
    return () => { document.title = previousTitle; };
  }, [route, displayName, t]);

  // --- Fetch route data ---
  useEffect(() => {
    const fetchRoute = async () => {
      try {
        const { doc, getDoc, getFirestore } = await import('firebase/firestore');
        const db = getFirestore(firebaseApp);
        const routeDoc = await getDoc(doc(db, 'routes', routeId));

        if (!routeDoc.exists()) { setError('Route not found'); return; }

        const data = { id: routeDoc.id, ...routeDoc.data() } as GpxRoute;
        setRoute(data);

        // Fetch GPX coordinates
        if (data.gpxUrl) {
          try {
            const gpxText = await fetchGpxWithProxy(data.gpxUrl);
            const parser = new DOMParser();
            const xml = parser.parseFromString(gpxText, 'application/xml');
            const trkpts = Array.from(xml.querySelectorAll('trkpt'));
            const coords = trkpts.map(pt => {
              const lat = parseFloat(pt.getAttribute('lat') ?? '');
              const lon = parseFloat(pt.getAttribute('lon') ?? '');
              return [lat, lon] as [number, number];
            }).filter(p => !isNaN(p[0]) && !isNaN(p[1]));
            setCoordinates(coords);

            // Enrich elevation profile on-demand if all zeros
            if (data.elevationProfile && data.elevationProfile.length > 0 && !data.elevationProfile.some((p: any) => p.elevation > 0) && coords.length > 0) {
              try {
                const step = Math.max(1, Math.floor(coords.length / 100));
                const sampleCoords = coords.filter((_: any, i: number) => i % step === 0);
                const elevations = await fetchBatchElevations(
                  sampleCoords.map(([lat, lon]: [number, number]) => ({ latitude: lat, longitude: lon }))
                );
                if (elevations && elevations.length === sampleCoords.length) {
                  // Build enriched profile
                  let totalDist = 0;
                  const profileSampleRate = Math.max(1, Math.floor(coords.length / 100));
                  const enrichedProfile = coords.map(([lat, lon]: [number, number], i: number) => {
                    if (i > 0) {
                      const [pLat, pLon] = coords[i - 1];
                      const R = 6371000;
                      const dLat = (lat - pLat) * Math.PI / 180;
                      const dLon = (lon - pLon) * Math.PI / 180;
                      const a = Math.sin(dLat / 2) ** 2 + Math.cos(pLat * Math.PI / 180) * Math.cos(lat * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
                      totalDist += R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
                    }
                    const sIdx = i / step;
                    const lower = Math.floor(sIdx);
                    const upper = Math.min(lower + 1, elevations.length - 1);
                    const frac = sIdx - lower;
                    const ele = Math.round(elevations[lower] * (1 - frac) + elevations[upper] * frac);
                    return { distance: Math.round(totalDist), elevation: ele };
                  }).filter((_: any, i: number) => i % profileSampleRate === 0);

                  data.elevationProfile = enrichedProfile;
                  setRoute({ ...data });

                  // Persist to Firestore (fire-and-forget)
                  const { doc: fbDoc, updateDoc: fbUpdate, getFirestore: fbFs } = await import('firebase/firestore');
                  fbUpdate(fbDoc(fbFs(firebaseApp), 'routes', routeId), { elevationProfile: enrichedProfile }).catch(() => {});
                }
              } catch (enrichErr) {
                console.warn('Elevation enrichment failed:', enrichErr);
              }
            }
          } catch (gpxErr) {
            console.warn('GPX fetch failed:', gpxErr);
          }
        }
      } catch (err) {
        console.error('Error fetching route:', err);
        setError('Failed to load route');
      } finally {
        setLoading(false);
      }
    };
    fetchRoute();
  }, [routeId]);

  // --- Record view (fire-and-forget, external traffic only) ---
  useEffect(() => {
    if (!route || !route.id || (route.status !== 'public' && route.status !== 'shared')) return;
    fetch('https://us-central1-fricamp-2810e.cloudfunctions.net/recordRouteView', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ routeId: route.id }),
    }).catch(() => { });
  }, [route?.id]);

  // Use persisted route attractions as source of truth for shared pages.
  // This keeps counts and items in sync with the main route view.
  useEffect(() => {
    setFetchedWikiSpots(persistedWikiSpots);
    if (persistedWikiSpots.length > 0) {
      loadedTabsRef.current.add('attractions');
    } else {
      loadedTabsRef.current.delete('attractions');
    }
  }, [route?.id, persistedWikiSpots]);

  // --- Fetch webcams along route (premium — krever innlogging) ---
  useEffect(() => {
    if (!isAuthed) return;
    if (coordinates.length < 2 && !route?.center) return;
    let cancelled = false;

    const loadWebcams = async () => {
      try {
        if (coordinates.length >= 2) {
          const routePath: Coordinates[] = coordinates.map(([lat, lon]) => ({ latitude: lat, longitude: lon }));
          const cams = await fetchCamerasNearRoute(routePath, 2000);
          if (!cancelled && cams.length > 0) {
            // Sort by distance along route (start → end)
            const ordered = cams.map(cam => {
              let bestSegDist = Infinity;
              let bestAlongRoute = 0;
              let cumDist = 0;
              for (let i = 1; i < routePath.length; i++) {
                const prev = routePath[i - 1];
                const curr = routePath[i];
                const segLen = getDistance(prev.latitude, prev.longitude, curr.latitude, curr.longitude);
                const d = getDistance(cam.latitude, cam.longitude, curr.latitude, curr.longitude);
                if (d < bestSegDist) {
                  bestSegDist = d;
                  bestAlongRoute = cumDist + segLen;
                }
                cumDist += segLen;
              }
              return { ...cam, _routeOrderM: bestAlongRoute };
            }).sort((a, b) => a._routeOrderM - b._routeOrderM);
            setWebcams(ordered);
            return;
          }
        }
        // Fallback: search near route center
        if (route?.center) {
          const searchRadius = Math.max(5000, (route.distance || 10) * 500);
          const cams = await fetchCamerasNearSpot(route.center, Math.min(searchRadius, 50000));
          if (!cancelled) setWebcams(cams);
        }
      } catch {
        // Webcams are non-critical
      }
    };

    loadWebcams();
    return () => { cancelled = true; };
  }, [isAuthed, coordinates, route?.center, route?.distance]);

  // --- Subscribe to reviews ---
  useEffect(() => {
    if (!routeId) return;
    const unsub = subscribeToRouteReviews(routeId, (r: any[]) => setReviews(r as UserReview[]));
    return () => unsub();
  }, [routeId]);

  // --- Fetch weather ---
  useEffect(() => {
    const loadWeather = async () => {
      if (coordinates.length === 0) return;
      setWeatherLoading(true);
      try {
        const lat = coordinates[0][0];
        const lon = coordinates[0][1];
        const weatherUrl = `https://api.met.no/weatherapi/locationforecast/2.0/compact?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`;
        const [response, snow] = await Promise.all([
          fetch(weatherUrl, { headers: { 'User-Agent': 'fricamp-app github.com/jon-takle' } }),
          fetchSnowDepth(lat, lon)
        ]);
        if (!response.ok) throw new Error(`weather_${response.status}`);
        const data = await response.json();
        setSnowData(snow);

        const timeseries = Array.isArray(data?.properties?.timeseries) ? data.properties.timeseries : [];
        const byDate = new Map<string, any[]>();
        timeseries.forEach((ts: any) => {
          const d = new Date(ts.time);
          const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          const list = byDate.get(key) || [];
          list.push(ts);
          byDate.set(key, list);
        });

        const locale = getUiLocale();
        const next7: WeatherDay[] = Array.from(byDate.entries()).slice(0, 7).map(([key, entries], idx) => {
          const temps = entries.map((e: any) => e?.data?.instant?.details?.air_temperature).filter((t: any) => typeof t === 'number') as number[];
          const midday = entries.find((e: any) => new Date(e.time).getHours() >= 12) || entries[0];
          const symbol = midday?.data?.next_12_hours?.summary?.symbol_code || midday?.data?.next_6_hours?.summary?.symbol_code || midday?.data?.next_1_hours?.summary?.symbol_code || 'cloudy';
          const date = new Date(entries[0].time);
          return {
            key,
            label: idx === 0 ? '__today__' : idx === 1 ? '__tomorrow__' : date.toLocaleDateString(locale, { weekday: 'short' }),
            maxTemp: temps.length ? Math.round(Math.max(...temps)) : 0,
            minTemp: temps.length ? Math.round(Math.min(...temps)) : 0,
            symbol
          };
        });
        setWeather7d(next7);

        const times = SunCalc.getTimes(new Date(), lat, lon);
        setSunData({
          sunrise: times.sunrise.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
          sunset: times.sunset.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
          daylightHours: Math.round(Math.max(0, times.sunset.getTime() - times.sunrise.getTime()) / (1000 * 60 * 60))
        });
      } catch (e) {
        console.warn('Weather fetch failed:', e);
      } finally {
        setWeatherLoading(false);
      }
    };
    loadWeather();
  }, [coordinates]);

  // --- Fetch facilities from external APIs ---
  useEffect(() => {
    if (coordinates.length < 2) return;
    const controller = new AbortController();
    const loadFacilities = async () => {
      setFacilitiesLoading(true);
      try {
        const pathCoords = coordinates.map(c => ({ latitude: c[0], longitude: c[1] }));
        const isNear = (point: { latitude: number; longitude: number }) =>
          isPointNearPolyline(point, pathCoords, 1500);

        // Single bounding box for all APIs (0.05 padding matches routeAnalysis.ts)
        const lats = coordinates.map(c => c[0]);
        const lons = coordinates.map(c => c[1]);
        const fullBounds = {
          latMin: Math.min(...lats) - 0.05,
          latMax: Math.max(...lats) + 0.05,
          lonMin: Math.min(...lons) - 0.05,
          lonMax: Math.max(...lons) + 0.05
        };

        // Initial fetch: essential data (Overpass POIs + Firestore cached spots)
        // NVDB rasteplasser er premium-lag – hentes kun for innloggede brukere.
        const tasks: Promise<any>[] = [
          fetchAllOverpassPOIs(fullBounds),
          getCachedSpotsInArea(fullBounds),
        ];
        if (isAuthed) tasks.push(fetchNVDBData(fullBounds));
        const results = await Promise.allSettled(tasks);
        const overpass = results[0];
        const fSpots = results[1];
        const nvdb = isAuthed ? results[2] : null;

        if (controller.signal.aborted) return;

        if (overpass.status === 'fulfilled') {
          setCampsites(overpass.value.campsites.filter(s => isNear(s.location)));
          setAmenities(overpass.value.amenities.filter(s => isNear(s.location)));
        }

        // wildSpots = only AI-found spots from Firebase cached_spots
        if (fSpots.status === 'fulfilled') {
          setWildSpots((fSpots.value as CampingSpot[]).filter(s => {
            const score = isNaN(s.score) ? 0 : Math.round(s.score);
            return (s.spotType === 'confirmed' || (s.spotType === 'algorithmic' && score >= 4)) && isNear(s.location);
          }));
        }

        // Rest areas from NVDB (Vegvesenet) — kun innloggede
        if (nvdb && nvdb.status === 'fulfilled') {
          setRestAreas(nvdb.value.objects.filter(o => isNear(o.location)));
        }

        loadedTabsRef.current.add('camping');
        loadedTabsRef.current.add('external_camping');
        loadedTabsRef.current.add('amenities');
        if (isAuthed) loadedTabsRef.current.add('rest');
      } catch (err) {
        console.warn('[SharedRoutePage] facility fetch failed:', err);
      } finally {
        if (!controller.signal.aborted) setFacilitiesLoading(false);
      }
    };
    loadFacilities();
    return () => controller.abort();
  }, [isAuthed, coordinates, route?.wikiSpots]);

  // --- Lazy-load deferred tab data (chargers, wiki, wildlife) on first tab click ---
  useEffect(() => {
    if (coordinates.length < 2 || loadedTabsRef.current.has(activeInfoTab)) return;
    loadedTabsRef.current.add(activeInfoTab);

    const pathCoords = coordinates.map(c => ({ latitude: c[0], longitude: c[1] }));
    const isNear = (point: { latitude: number; longitude: number }) =>
      isPointNearPolyline(point, pathCoords, 1500);
    const lats = coordinates.map(c => c[0]);
    const lons = coordinates.map(c => c[1]);
    const fullBounds = {
      latMin: Math.min(...lats) - 0.05, latMax: Math.max(...lats) + 0.05,
      lonMin: Math.min(...lons) - 0.05, lonMax: Math.max(...lons) + 0.05
    };

    // Premium-tabs krever innlogging (rest, chargers, wildlife, bathing).
    // attractions (wiki) holdes åpent for SEO.
    const PREMIUM_TABS = new Set(['rest', 'chargers', 'wildlife', 'bathing']);
    if (PREMIUM_TABS.has(activeInfoTab) && !isAuthed) {
      loadedTabsRef.current.delete(activeInfoTab);
      return;
    }

    const load = async () => {
      setDeferredLoading(prev => new Set([...prev, activeInfoTab]));
      try {
        if (activeInfoTab === 'rest') {
          const result = await fetchNVDBData(fullBounds);
          setRestAreas(result.objects.filter(o => isNear(o.location)));
        } else if (activeInfoTab === 'chargers') {
          const result = await fetchChargersInBounds(fullBounds);
          setChargers(result.filter(c => isNear(c.location)));
        } else if (activeInfoTab === 'attractions') {
          if (persistedWikiSpots.length > 0) {
            setFetchedWikiSpots(persistedWikiSpots);
          } else {
            const result = await fetchWikiForPath(pathCoords);
            setFetchedWikiSpots(normalizeWikiSpots(result));
          }
        } else if (activeInfoTab === 'wildlife') {
          const result = await fetchWildlifeForRoute(pathCoords);
          setWildlife(result);
        } else if (activeInfoTab === 'bathing') {
          const [osmResult, havResult] = await Promise.all([
            fetchAllNorwegianBathingPlaces(),
            fetchSwedishBathingPlaces(),
          ]);
          const filterInBounds = (places: typeof osmResult) => places.filter(p =>
            p.location.latitude >= fullBounds.latMin && p.location.latitude <= fullBounds.latMax &&
            p.location.longitude >= fullBounds.lonMin && p.location.longitude <= fullBounds.lonMax
          );
          const all = [...filterInBounds(osmResult), ...filterInBounds(havResult)].filter(bp => isNear(bp.location));
          setBathingPlaces(all);
        }
      } catch (err) {
        console.warn(`[SharedRoutePage] deferred ${activeInfoTab} fetch failed:`, err);
      } finally {
        setDeferredLoading(prev => { const next = new Set(prev); next.delete(activeInfoTab); return next; });
      }
    };
    load();
  }, [isAuthed, activeInfoTab, coordinates, persistedWikiSpots]);

  // --- Smart auto-load wildlife: only when route.stats says there are observations.
  // Keeps SEO landing pages fast on routes without wildlife, while matching the
  // in-app map experience (auto-shown wildlife markers) where it matters.
  // Premium-lag — kun innloggede.
  useEffect(() => {
    if (!isAuthed) return;
    if (coordinates.length < 2) return;
    if (loadedTabsRef.current.has('wildlife')) return;
    const expected = route?.stats?.wildlifeCount ?? 0;
    if (expected <= 0) return;

    loadedTabsRef.current.add('wildlife');
    const pathCoords = coordinates.map(c => ({ latitude: c[0], longitude: c[1] }));
    (async () => {
      try {
        const result = await fetchWildlifeForRoute(pathCoords);
        setWildlife(result);
      } catch (err) {
        console.warn('[SharedRoutePage] auto-load wildlife failed:', err);
      }
    })();
  }, [isAuthed, coordinates, route?.stats?.wildlifeCount]);

  // --- Computed values ---
  const difficultyColor = useMemo(() => {
    if (!route) return '';
    switch (route.difficulty) {
      case 'Easy': return 'text-emerald-400 border-emerald-500/30 bg-emerald-500/10';
      case 'Medium': return 'text-blue-400 border-blue-500/30 bg-blue-500/10';
      case 'Hard': return 'text-orange-400 border-orange-500/30 bg-orange-500/10';
      case 'Extreme': return 'text-red-400 border-red-500/30 bg-red-500/10';
      default: return 'text-[var(--text-secondary)] border-[var(--border-secondary)] bg-[var(--bg-surface-alt)]';
    }
  }, [route]);

  const routeLineColor = useMemo(() => {
    if (!route) return '#22c55e';
    switch (route.difficulty) {
      case 'Easy': return '#22c55e';
      case 'Medium': return '#3b82f6';
      case 'Hard': return '#ef4444';
      case 'Extreme': return '#7f1d1d';
      default: return '#22c55e';
    }
  }, [route]);

  const facilityCounts = useMemo(() => {
    const routeStats = route?.stats;
    const persistedAttractionCount = persistedWikiSpots.length;
    // Use live data when available (max of live + stored to avoid undercounting),
    // fall back to stored stats before fetch completes
    const liveOrStored = (live: number, stored: number | undefined) =>
      live > 0 ? Math.max(live, stored ?? 0) : (typeof stored === 'number' ? stored : 0);
    return {
      wildSpots: liveOrStored(wildSpots.length, routeStats?.wildSpotsCount),
      restAreas: liveOrStored(restAreas.length, routeStats?.restAreasCount),
      campsites: liveOrStored(campsites.length, routeStats?.campsitesCount),
      attractions: persistedAttractionCount > 0
        ? persistedAttractionCount
        : liveOrStored(fetchedWikiSpots.length, routeStats?.attractionsCount),
      chargers: liveOrStored(chargers.length, routeStats?.chargersCount),
      wildlife: liveOrStored(wildlife.length, routeStats?.wildlifeCount),
      amenities: amenities.length,
      bathing: bathingPlaces.length
    };
  }, [
    route?.stats,
    persistedWikiSpots,
    wildSpots.length,
    restAreas.length,
    campsites.length,
    fetchedWikiSpots.length,
    chargers.length,
    wildlife.length,
    amenities.length,
    bathingPlaces.length
  ]);

  // Extract peaks from elevation profile
  const peaks = useMemo(() => {
    if (!route?.elevationProfile || !coordinates.length) return [];
    const profile = route.elevationProfile;
    if (profile.length < 10) return [];

    // Find local maxima above 400m
    const candidates: { idx: number; elevation: number; coord: [number, number] }[] = [];
    for (let i = 5; i < profile.length - 5; i++) {
      const elev = profile[i].elevation;
      if (elev < 400) continue;
      const isLocalMax = profile.slice(i - 5, i).every(p => p.elevation <= elev) &&
        profile.slice(i + 1, i + 6).every(p => p.elevation <= elev);
      if (isLocalMax && coordinates[i]) {
        candidates.push({ idx: i, elevation: elev, coord: coordinates[i] });
      }
    }
    if (candidates.length === 0) return [];

    // Sort by elevation, take top peak + secondaries >80% height and >3km apart
    candidates.sort((a, b) => b.elevation - a.elevation);
    const highest = candidates[0];
    const result = [{ ...highest, isHighest: true }];
    for (const c of candidates.slice(1)) {
      if (c.elevation < highest.elevation * 0.8) continue;
      const dist = Math.sqrt(
        Math.pow((c.coord[0] - highest.coord[0]) * 111000, 2) +
        Math.pow((c.coord[1] - highest.coord[1]) * 111000 * Math.cos(c.coord[0] * Math.PI / 180), 2)
      );
      if (dist > 3000) result.push({ ...c, isHighest: false });
      if (result.length >= 3) break;
    }
    return result;
  }, [route?.elevationProfile, coordinates]);

  const mediaSlides = useMemo<MediaSlide[]>(() => {
    const slides: MediaSlide[] = [];
    const seen = new Set<string>();

    // 1. Route images first
    (route?.imageUrls || []).filter(url => url.trim()).forEach(url => {
      if (seen.has(url)) return;
      seen.add(url);
      slides.push({ type: 'image', url });
    });

    // 2. Webcam images (first 3 shown normally, rest as blurred teasers)
    const visibleCams = webcams.filter(c => !failedWebcamIds.has(c.id));
    visibleCams.slice(0, 3).forEach(cam => {
      if (seen.has(cam.image_url)) return;
      seen.add(cam.image_url);
      slides.push({ type: 'webcam', url: cam.image_url, name: cam.name, roadName: cam.road_name });
    });

    // 3. Attraction images from wiki spots
    fetchedWikiSpots
      .filter(w => w.thumbnail?.source && w.url)
      .slice(0, 6)
      .forEach(w => {
        const url = w.thumbnail!.source;
        if (seen.has(url)) return;
        seen.add(url);
        slides.push({ type: 'attraction-image', url, link: w.url, title: w.title });
      });

    return slides;
  }, [route?.imageUrls, fetchedWikiSpots, webcams, failedWebcamIds]);

  const avgRating = useMemo(() => {
    if (reviews.length === 0) return 0;
    return reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length;
  }, [reviews]);

  // --- Sorted wild spots (best score first) ---
  const sortedWildSpots = useMemo(() =>
    [...wildSpots].sort((a, b) => (isNaN(b.score) ? 0 : b.score) - (isNaN(a.score) ? 0 : a.score)),
    [wildSpots]
  );

  const handleOpenInAppAndFavorite = useCallback((e: React.MouseEvent, spotId: string) => {
    e.stopPropagation();
    const url = new URL(`/app/open/spot/${encodeURIComponent(spotId)}`, window.location.origin);
    url.searchParams.set('favorite', '1');
    url.searchParams.set('source', 'shared_route');
    url.searchParams.set('lang', appLang);
    window.location.assign(url.toString());
  }, [appLang]);

  // --- Image carousel handlers ---
  const goPrevImage = () => {
    if (mediaSlides.length < 2) return;
    setActiveImageIndex(prev => (prev - 1 + mediaSlides.length) % mediaSlides.length);
  };
  const goNextImage = () => {
    if (mediaSlides.length < 2) return;
    setActiveImageIndex(prev => (prev + 1) % mediaSlides.length);
  };

  useEffect(() => {
    if (mediaSlides.length < 2) return;
    const interval = setInterval(goNextImage, 5000);
    return () => clearInterval(interval);
  }, [mediaSlides.length]);

  const handleMediaTouchStart = (e: React.TouchEvent) => {
    swipeStartXRef.current = e.changedTouches?.[0]?.clientX ?? null;
  };
  const handleMediaTouchEnd = (e: React.TouchEvent) => {
    if (swipeStartXRef.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX;
    if (typeof endX !== 'number') return;
    const delta = endX - swipeStartXRef.current;
    if (delta <= -45) goNextImage();
    else if (delta >= 45) goPrevImage();
    swipeStartXRef.current = null;
  };

  // Ref for tab container (scroll target from map marker clicks)
  const tabContainerRef = useRef<HTMLDivElement>(null);

  // --- Item toggle ---
  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const PAGE_SIZE_CONST = 20;

  /** Called when a map marker is clicked: switches tab, paginates, expands item, scrolls */
  const focusListItem = useCallback((tab: typeof activeInfoTab, itemId: string, itemIndex: number) => {
    setActiveInfoTab(tab);
    const pg = Math.floor(itemIndex / PAGE_SIZE_CONST);
    setTabPage(prev => ({ ...prev, [tab]: pg }));
    setExpandedIds(prev => { const next = new Set(prev); next.add(itemId); return next; });
    setTimeout(() => {
      tabContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  // --- Elevation profile markers ---
  const elevationMarkers = useMemo<ElevationMarker[]>(() => {
    if (!route?.elevationProfile || coordinates.length === 0) return [];
    const profile = route.elevationProfile;
    const result: ElevationMarker[] = [];

    sortedWildSpots.forEach(spot => {
      const dist = getDistanceAlongRoute(spot.location.latitude, spot.location.longitude, coordinates, profile);
      if (dist !== null) result.push({ id: `wildspot-${spot.id}`, distanceKm: dist, category: 'wildspot', label: spot.name });
    });

    fetchedWikiSpots.forEach(spot => {
      const dist = getDistanceAlongRoute(spot.location.latitude, spot.location.longitude, coordinates, profile);
      if (dist !== null) result.push({ id: `wiki-${spot.pageid}`, distanceKm: dist, category: 'attraction', label: spot.title });
    });

    restAreas.forEach(ra => {
      const dist = getDistanceAlongRoute(ra.location.latitude, ra.location.longitude, coordinates, profile);
      if (dist !== null) result.push({ id: `rest-${ra.id}`, distanceKm: dist, category: 'rest', label: ra.name || t('shared.rest_area') });
    });

    return result;
  }, [route?.elevationProfile, coordinates, sortedWildSpots, fetchedWikiSpots, restAreas, t]);

  // --- Parse start/end place names from description ---
  const { startLabel, endLabel } = useMemo(() => {
    if (!route) return { startLabel: undefined, endLabel: undefined };
    // Prefer stored fields if available
    if (route.startPlace && route.endPlace) {
      return { startLabel: route.startPlace, endLabel: route.endPlace };
    }
    // Parse from description: "... fra Flekkefjord til Bore."
    const match = route.description?.match(/fra\s+(.+?)\s+til\s+(.+?)\.?\s*$/i);
    if (match) return { startLabel: match[1].trim(), endLabel: match[2].trim() };
    return { startLabel: undefined, endLabel: undefined };
  }, [route]);

  const handleElevationMarkerClick = useCallback((markerId: string) => {
    if (markerId.startsWith('wildspot-')) {
      const idx = sortedWildSpots.findIndex(s => `wildspot-${s.id}` === markerId);
      if (idx >= 0) focusListItem('camping', markerId, idx);
    } else if (markerId.startsWith('wiki-')) {
      const idx = fetchedWikiSpots.findIndex(s => `wiki-${s.pageid}` === markerId);
      if (idx >= 0) focusListItem('attractions', markerId, idx);
    } else if (markerId.startsWith('rest-')) {
      const idx = restAreas.findIndex(r => `rest-${r.id}` === markerId);
      if (idx >= 0) focusListItem('rest', markerId, idx);
    }
  }, [sortedWildSpots, fetchedWikiSpots, restAreas, focusListItem]);

  // --- Loading state ---
  if (loading) {
    return (
      <div className="h-screen w-full bg-[#4b5a43] flex items-center justify-center">
        <div className="text-center space-y-3">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin mx-auto" />
          <p className="text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">{t('shared.loading_route')}</p>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (error || !route) {
    return (
      <div className="h-screen w-full bg-[#4b5a43] flex flex-col items-center justify-center p-6 text-center">
        <h1 className="text-xl font-black text-[var(--text-primary)] mb-2">{error || t('shared.route_not_found')}</h1>
        <p className="text-[var(--text-secondary)] text-sm mb-6">{t('shared.link_expired')}</p>
        <a href={withLang('/')} className="px-6 py-3 bg-emerald-600 text-white rounded-2xl font-bold transition-transform active:scale-95 inline-flex items-center gap-2">
          <ArrowLeft size={16} /> {t('shared.go_to_fricamp')}
        </a>
      </div>
    );
  }



  return (
    <div className="h-screen w-full overflow-y-auto overflow-x-hidden bg-[#4b5a43] text-[var(--text-primary)] font-sans selection:bg-emerald-500/30" style={{ WebkitOverflowScrolling: 'touch' }}>
      <div className="min-h-full bg-[radial-gradient(circle_at_top,#5a6c4f_0%,#46563f_45%,#32402f_100%)]">
        {/* 1. Sticky Header */}
        <div className="p-4 border-b border-[var(--border-primary)] flex items-center justify-between sticky top-0 bg-[#4b5a43]/80 backdrop-blur-xl z-[1000] animate-in fade-in duration-500">
          <div className="flex items-center gap-3">
            <a href={withLang('/')} className="p-2 bg-[var(--bg-input)] hover:bg-[var(--bg-input)] rounded-xl text-[var(--text-primary)] transition-colors border border-[var(--border-primary)]">
              <ArrowLeft size={18} />
            </a>
            <button
              onClick={toggleLanguage}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-tiny font-black uppercase tracking-wider hover:bg-[var(--bg-surface)] transition-all active:scale-95"
            >
              <Globe size={14} className="text-emerald-400" />
              {currentLanguage.startsWith('no') ? 'EN' : currentLanguage.startsWith('en') ? 'DE' : 'NO'}
            </button>
            {!currentLanguage.startsWith('no') && !contentTranslated && fetchedWikiSpots.length > 0 && (
              <button
                onClick={handleTranslateContent}
                disabled={contentTranslating}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-violet-500/15 border border-violet-500/30 text-2xs font-bold text-violet-300 hover:bg-violet-500/25 transition-all active:scale-95 disabled:opacity-50"
              >
                {contentTranslating ? (
                  <><Loader2 size={12} className="animate-spin" /> {t('shared.translating')}</>
                ) : (
                  <><Globe size={12} /> {t('shared.translate_content')}</>
                )}
              </button>
            )}
            <span className="text-2xs font-black uppercase tracking-widest text-emerald-500">{t('shared.badge')}</span>
          </div>
          <a href={fricampWebUrl} target="_blank" rel="noopener noreferrer" className="w-14 h-14 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border-secondary)] overflow-hidden flex items-center justify-center hover:scale-105 transition-transform hover:bg-white/15 hover:border-emerald-500/30 shadow-lg">
            <img src="/Fricamplogo.png" alt="Fricamp" className="w-full h-full object-cover scale-150 drop-shadow-md" />
          </a>
        </div>

        <div className="max-w-[760px] mx-auto w-full pb-32 px-4">
          {/* 2. Route Title + Region + Creator + Chips */}
          <div className="space-y-4 pt-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div>
              <span className="px-2 py-0.5 rounded-md bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] text-2xs font-black uppercase tracking-widest text-[var(--text-secondary)]">
                {route.region ? t(`regions.${route.region}`) : t('shared.route_suggestion')}
              </span>
              <h1 className="text-4xl md:text-5xl font-grunge uppercase tracking-wider text-[var(--text-primary)] drop-shadow-lg leading-tight mt-2">{displayName}</h1>
              {translating && (
                <p className="text-3xs text-[var(--text-muted)] italic mt-1 flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> {t('shared.translating')}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <div className="w-7 h-7 rounded-full overflow-hidden border border-[var(--border-secondary)] bg-[var(--bg-input)] flex items-center justify-center shrink-0">
                  {route.createdByPhoto ? (
                    <img src={route.createdByPhoto} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <User size={14} className="text-[var(--text-muted)]" />
                  )}
                </div>
                <p className="text-tiny font-bold text-[var(--text-muted)] uppercase tracking-widest">{t('shared.shared_by', { name: route.createdByName || t('route.anonymous') })}</p>
                {route.creatorVehicleCategory && (
                  <VehicleBadge category={route.creatorVehicleCategory} length={route.creatorVehicleLength} is4wd={route.creatorVehicleIs4wd} />
                )}
              </div>
            </div>

            <div className="flex gap-2 flex-wrap">
              <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${difficultyColor}`}>
                <Activity size={12} />
                <span className="text-2xs font-black uppercase tracking-widest">{t(`route.${(route.difficulty || 'Medium').toLowerCase()}`)}</span>
              </div>
              {route.distance && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]">
                  <MapPin size={12} />
                  <span className="text-2xs font-black uppercase tracking-widest">{route.distance.toFixed(1)} km</span>
                </div>
              )}
              {route.estimatedTime && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]">
                  <Clock size={12} />
                  <span className="text-2xs font-black uppercase tracking-widest">{route.estimatedTime}</span>
                </div>
              )}
              {reviews.length > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-500/30 bg-amber-500/10 text-amber-300">
                  <Star size={12} />
                  <span className="text-2xs font-black uppercase tracking-widest">{avgRating.toFixed(1)} ({reviews.length})</span>
                </div>
              )}
              {route.view_count != null && route.view_count > 0 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface-alt)] text-[var(--text-secondary)]">
                  <Eye size={12} />
                  <span className="text-2xs font-black uppercase tracking-widest">{route.view_count} {t('route.views')}</span>
                </div>
              )}
            </div>

            {/* 3D Flyover CTA */}
            {route?.elevationProfile && route.elevationProfile.length >= 2 && route.gpxUrl && (
              <button
                onClick={() => setShowFlyover(true)}
                className="w-full mt-4 py-3 px-4 rounded-2xl bg-gradient-to-r from-emerald-600/20 to-cyan-600/20 border border-emerald-500/30 hover:border-emerald-400/50 flex items-center justify-center gap-2.5 text-emerald-300 hover:text-emerald-200 transition-all active:scale-[0.98] group"
              >
                <Orbit size={18} className="group-hover:animate-spin" style={{ animationDuration: '3s' }} />
                <span className="text-tiny font-black uppercase tracking-widest">3D Flyover</span>
                <ChevronRight size={14} className="opacity-50 group-hover:translate-x-0.5 transition-transform" />
              </button>
            )}
          </div>

          {/* 3. Media Carousel */}
          {mediaSlides.length > 0 && (
            <div
              className="relative h-[280px] md:h-[420px] overflow-hidden bg-black rounded-panel shadow-2xl mt-4 animate-in fade-in slide-in-from-bottom-4 duration-700"
              onTouchStart={handleMediaTouchStart}
              onTouchEnd={handleMediaTouchEnd}
            >
              {mediaSlides.map((slide, idx) => (
                <div
                  key={`${slide.type}-${idx}`}
                  className={`absolute inset-0 transition-all duration-500 ease-out ${idx === activeImageIndex ? 'opacity-100 scale-100 z-10' : 'opacity-0 scale-[1.02] z-0 pointer-events-none'}`}
                >
                  {slide.type === 'image' && (
                    <img src={slide.url} alt={`${route.name} ${idx + 1}`} className="w-full h-full object-cover" />
                  )}
                  {slide.type === 'webcam' && (
                    <div className="relative w-full h-full">
                      <img
                        src={slide.url}
                        alt={slide.name}
                        className="w-full h-full object-cover"
                        onError={() => setFailedWebcamIds(prev => new Set(prev).add(slide.url))}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4 flex flex-col justify-end">
                        <span className="text-3xs font-black uppercase tracking-widest text-cyan-300">{t('shared.webcam_label')}</span>
                        <span className="text-sm font-bold text-white">{slide.name}</span>
                        {slide.roadName && <span className="text-2xs text-white/60 mt-0.5">{slide.roadName}</span>}
                        <span className="text-[7px] text-white/40 mt-1">{t('shared.source_svv')}</span>
                      </div>
                    </div>
                  )}
                  {slide.type === 'attraction-image' && (
                    <a href={slide.link} target="_blank" rel="noopener noreferrer" className="relative block w-full h-full group">
                      <img src={slide.url} alt={slide.title} className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent p-4 flex flex-col justify-end">
                        <span className="text-3xs font-black uppercase tracking-widest text-emerald-300">{t('spot.attraction')}</span>
                        <span className="text-sm font-bold text-white flex items-center gap-2">
                          {slide.title}
                          <ExternalLink size={12} className="opacity-80" />
                        </span>
                      </div>
                    </a>
                  )}
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none z-20" />
              {mediaSlides.length > 1 && (
                <>
                  <button onClick={goPrevImage} className="absolute left-3 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/55 border border-white/25 text-white hover:bg-black/70"><ChevronLeft size={16} /></button>
                  <button onClick={goNextImage} className="absolute right-3 top-1/2 -translate-y-1/2 z-30 p-2 rounded-full bg-black/55 border border-white/25 text-white hover:bg-black/70"><ChevronRight size={16} /></button>
                  <div className="absolute bottom-3 right-3 z-30 px-2 py-1 rounded-lg bg-black/55 border border-[var(--border-secondary)] text-2xs font-black tracking-wider">
                    {activeImageIndex + 1}/{mediaSlides.length}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Webcam gallery — horizontal scroll, first 3 clear, rest blurred with Fricamp logo */}
          {(() => {
            const visibleCams = webcams.filter(c => !failedWebcamIds.has(c.id));
            if (visibleCams.length === 0) return null;
            return (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 px-1">
                  <span className="text-3xs font-black uppercase tracking-widest text-cyan-400">{t('shared.webcams_along_route')}</span>
                  <span className="text-4xs font-black text-cyan-300/60 bg-cyan-400/10 px-2 py-0.5 rounded-md">{visibleCams.length}</span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(6,182,212,0.3) transparent' }}>
                  {visibleCams.map((cam, idx) => {
                    const isBlurred = idx >= 3;
                    return (
                      <div key={cam.id} className="relative rounded-2xl overflow-hidden border border-cyan-500/20 bg-black shrink-0 w-[200px] h-[120px]">
                        <img
                          src={cam.image_url}
                          alt={cam.name}
                          className={`w-full h-full object-cover ${isBlurred ? 'blur-[6px] scale-110' : ''}`}
                          loading="lazy"
                          onError={() => setFailedWebcamIds(prev => new Set(prev).add(cam.id))}
                        />
                        {isBlurred && (
                          <div className="absolute inset-0 bg-black/40 flex flex-col items-center justify-center gap-1.5">
                            <img
                              src="/Fricamplogo.png"
                              alt="Fricamp"
                              className="w-7 h-7 rounded-lg opacity-90"
                            />
                            <span className="text-[7px] font-black uppercase tracking-widest text-white/70">{t('shared.open_in_app')}</span>
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-3xs font-bold text-white truncate">{cam.name}</p>
                          {cam.road_name && <p className="text-[7px] text-white/50">{cam.road_name}</p>}
                        </div>
                        <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-black/60 text-cyan-200 text-[7px] font-black uppercase tracking-widest border border-white/10">
                          #{idx + 1}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[7px] text-white/30 px-1">{t('shared.source_svv')}</p>
              </div>
            );
          })()}

          {/* 3. Rich Map */}
          <div className="h-[280px] md:h-[400px] relative w-full overflow-hidden shadow-2xl rounded-panel bg-[var(--bg-surface)] flex items-center justify-center mt-4">
            {coordinates.length > 0 ? (
              <MapContainer key={mapType} center={[59.9139, 10.7522]} zoom={13} style={{ height: '100%', width: '100%' }} zoomControl={false} maxZoom={21} minZoom={4}>
                <MapSizeFixer />
                <ZoomControl position="bottomright" />
                {mapType === 'topo' && (
                  <>
                    <TileLayer url={MAP_LAYERS.NORWAY_TOPO} attribution='&copy; Kartverket' maxZoom={21} maxNativeZoom={18} />
                    <TileLayer url={MAP_LAYERS.TOPO} opacity={0.5} attribution='&copy; OpenTopoMap' maxZoom={21} maxNativeZoom={17} />
                  </>
                )}
                {mapType === 'satellite' && (
                  <>
                    <TileLayer url={MAP_LAYERS.SATELLITE} attribution='&copy; ESRI' maxZoom={21} maxNativeZoom={18} />
                    <TileLayer url={MAP_LAYERS.SATELLITE_LABELS} attribution='&copy; ESRI' maxZoom={21} maxNativeZoom={18} />
                  </>
                )}
                {mapType === 'dark' && (
                  <TileLayer url={MAP_LAYERS.DARK} attribution='&copy; CARTO' maxZoom={21} maxNativeZoom={18} />
                )}
                <Polyline positions={coordinates} color={routeLineColor} weight={7} opacity={0.9} />
                <FitBounds coords={coordinates} />

                {/* Start marker */}
                {coordinates.length > 0 && (
                  <Marker
                    position={coordinates[0]}
                    icon={L.divIcon({
                      className: 'custom-div-icon',
                      html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;background:#22c55e;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"><span style="font-size:14px;font-weight:900;color:white;">▶</span></div>`,
                      iconSize: [32, 32],
                      iconAnchor: [16, 16]
                    })}
                  >
                    <Popup><div className="p-1 text-xs font-bold">{t('shared.start')}</div></Popup>
                  </Marker>
                )}

                {/* End marker */}
                {coordinates.length > 1 && (
                  <Marker
                    position={coordinates[coordinates.length - 1]}
                    icon={L.divIcon({
                      className: 'custom-div-icon',
                      html: `<div style="display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;background:#ef4444;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"><span style="font-size:14px;font-weight:900;color:white;">⏹</span></div>`,
                      iconSize: [32, 32],
                      iconAnchor: [16, 16]
                    })}
                  >
                    <Popup><div className="p-1 text-xs font-bold">{t('shared.end')}</div></Popup>
                  </Marker>
                )}

                {/* Peak markers */}
                {peaks.map((peak, idx) => (
                  <Marker
                    key={`peak-${idx}`}
                    position={peak.coord}
                    icon={L.divIcon({
                      className: 'custom-div-icon',
                      html: `<div style="display:flex;align-items:center;justify-content:center;width:${peak.isHighest ? 36 : 28}px;height:${peak.isHighest ? 36 : 28}px;border-radius:50%;background:${peak.isHighest ? '#f59e0b' : '#78716c'};border:2px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);"><span style="font-size:${peak.isHighest ? 9 : 8}px;font-weight:900;color:white;">${Math.round(peak.elevation)}m</span></div>`,
                      iconSize: [peak.isHighest ? 36 : 28, peak.isHighest ? 36 : 28],
                      iconAnchor: [peak.isHighest ? 18 : 14, peak.isHighest ? 18 : 14]
                    })}
                  >
                    <Popup><div className="p-1 text-xs font-bold">{t('shared.altitude', { meters: Math.round(peak.elevation) })}</div></Popup>
                  </Marker>
                ))}

                {/* Campsites cluster */}
                {campsites.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => createCustomMarker('fa-solid fa-caravan', '#1565C0', false, false, undefined, cluster.getChildCount())}>
                    {campsites.map((spot, idx) => (
                      <Marker key={`camp-${spot.id}`} position={[spot.location.latitude, spot.location.longitude]}
                        eventHandlers={{ click: () => focusListItem('camping', `camp-${spot.id}`, idx) }}
                        icon={createCustomMarker('fa-solid fa-caravan', '#1565C0', false)} />
                    ))}
                  </MarkerClusterGroup>
                )}

                {/* Rest areas cluster */}
                {restAreas.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => createCustomMarker('fa-solid fa-coffee', '#1565C0', false, false, undefined, cluster.getChildCount())}>
                    {restAreas.map((ra, idx) => {
                      const ri = ra.rasteplassInfo;
                      const typeLabel = ri?.type ? (() => {
                        const key = `shared.rest_type_${ri.type.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '')}`;
                        const translated = t(key, '');
                        return translated || ri.type;
                      })() : null;
                      return (
                        <Marker key={`rest-${ra.id}`} position={[ra.location.latitude, ra.location.longitude]}
                          eventHandlers={{ click: () => focusListItem('rest', `rest-${ra.id}`, idx) }}
                          icon={createCustomMarker('fa-solid fa-coffee', '#1565C0', false)}>
                          <Popup>
                            <div style={{ minWidth: 160, fontFamily: 'system-ui, sans-serif', color: '#1c1917' }}>
                              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{ra.name || t('shared.rest_area')}</div>
                              {typeLabel && <div style={{ fontSize: 11, color: '#78716c', marginBottom: 6 }}>{typeLabel}{ri?.kapasitet && ri.kapasitet > 0 ? ` · ${t('shared.capacity', { count: ri.kapasitet })}` : ''}</div>}
                              {ri && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                  {ri.toalett && <span style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6, padding: '2px 6px', fontSize: 11, color: '#1e40af' }}>🚻 {t('shared.toilet')}</span>}
                                  {ri.mobler && <span style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6, padding: '2px 6px', fontSize: 11, color: '#1e40af' }}>🪑 {t('shared.bench')}</span>}
                                  {ri.soppel && <span style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6, padding: '2px 6px', fontSize: 11, color: '#1e40af' }}>🗑️ {t('shared.trash')}</span>}
                                  {ri.belysning && <span style={{ background: '#dbeafe', border: '1px solid #93c5fd', borderRadius: 6, padding: '2px 6px', fontSize: 11, color: '#1e40af' }}>💡 {t('shared.lighting')}</span>}
                                  {ri.vinterstengt && <span style={{ background: '#ffedd5', border: '1px solid #fdba74', borderRadius: 6, padding: '2px 6px', fontSize: 11, color: '#9a3412' }}>❄️ {t('shared.winter_closed')}</span>}
                                </div>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      );
                    })}
                  </MarkerClusterGroup>
                )}

                {/* Chargers cluster */}
                {chargers.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => createCustomMarker('fa-solid fa-bolt', '#F9A825', false, false, undefined, cluster.getChildCount())}>
                    {chargers.map((c, idx) => (
                      <Marker key={`chg-${c.id}`} position={[c.location.latitude, c.location.longitude]}
                        eventHandlers={{ click: () => focusListItem('chargers', `chg-${c.id}`, idx) }}
                        icon={chargerIcon(c)} />
                    ))}
                  </MarkerClusterGroup>
                )}

                {/* Wiki attractions cluster */}
                {fetchedWikiSpots.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => createCustomMarker('fa-solid fa-landmark', '#8b5cf6', false, false, undefined, cluster.getChildCount())}>
                    {fetchedWikiSpots.map((spot, idx) => (
                      <Marker key={`wiki-${spot.pageid}`} position={[spot.location.latitude, spot.location.longitude]}
                        eventHandlers={{ click: () => focusListItem('attractions', `wiki-${spot.pageid}`, idx) }}
                        icon={wikiIcon(spot)} />
                    ))}
                  </MarkerClusterGroup>
                )}

                {/* Wildlife cluster (indigo - matches main app #4f46e5) */}
                {wildlife.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => L.divIcon({ html: `<div style="display:flex;align-items:center;justify-content:center;width:38px;height:38px;border-radius:50%;background:#4338ca;border:2px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);font-size:12px;font-weight:900;color:white;">🐾 ${cluster.getChildCount()}</div>`, className: '', iconSize: L.point(38, 38) })}>
                    {wildlife
                      .filter((w) => !!w?.location && Number.isFinite(w.location.latitude) && Number.isFinite(w.location.longitude))
                      .map((w, idx) => (
                      <Marker key={`wild-${w.id}`} position={[w.location.latitude, w.location.longitude]}
                        eventHandlers={{ click: () => focusListItem('wildlife', `wild-${w.id}`, idx) }}
                        icon={L.divIcon({ className: '', html: `<div style="width:32px;height:32px;border-radius:50%;background:#4f46e5;border:2px solid rgba(255,255,255,0.7);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,0.35);font-size:16px;cursor:pointer;">${w.emoji}</div>`, iconSize: [32, 32], iconAnchor: [16, 16] })} />
                    ))}
                  </MarkerClusterGroup>
                )}

                {/* Amenities cluster (bålplass, gapahuk, utsikt, etc.) */}
                {amenities.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => createCustomMarker('fa-solid fa-layer-group', '#44403c', false, false, undefined, cluster.getChildCount())}>
                    {amenities.map((a, idx) => {
                      const f = (a.features || []).join(' ').toUpperCase();
                      const isFire = f.includes('BÅLPLASS') || f.includes('FIREPIT');
                      const isView = f.includes('UTSIKT') || f.includes('VIEWPOINT');
                      const isShelter = f.includes('GAPAHUK') || f.includes('SHELTER');
                      const ic = isFire ? 'fa-solid fa-fire' : isView ? 'fa-solid fa-mountain' : isShelter ? 'fa-solid fa-person-shelter' : 'fa-solid fa-utensils';
                      const cl = isFire ? '#f97316' : isView ? '#0ea5e9' : isShelter ? '#0d9488' : '#3b82f6';
                      return (
                        <Marker key={`amen-${a.id}`} position={[a.location.latitude, a.location.longitude]}
                          eventHandlers={{ click: () => focusListItem('amenities', `amen-${a.id}`, idx) }}
                          icon={createCustomMarker(ic, cl, false)} />
                      );
                    })}
                  </MarkerClusterGroup>
                )}

                {/* Bathing places cluster */}
                {bathingPlaces.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => L.divIcon({ html: `<div style="display:flex;align-items:center;justify-content:center;width:38px;height:38px;border-radius:50%;background:#0ea5e9;border:2px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);font-size:12px;font-weight:900;color:white;"><i class="fa-solid fa-person-swimming" style="font-size:12px"></i>&nbsp;${cluster.getChildCount()}</div>`, className: '', iconSize: L.point(38, 38) })}>
                    {bathingPlaces.map((bp, idx) => {
                      const hasTemp = bp.waterTemperature != null && bp.waterTemperature > 0;
                      const hasWarning = bp.waterQualityWarning || false;
                      const color = hasWarning ? '#ef4444' : '#0ea5e9';
                      const tempBadge = hasTemp
                        ? `<div style="position:absolute;bottom:-6px;right:-8px;background:white;border:1.5px solid ${color};border-radius:8px;padding:0 3px;font-size:8px;font-weight:900;color:${color};line-height:14px;white-space:nowrap;">🌡${bp.waterTemperature!.toFixed(0)}°</div>`
                        : '';
                      return (
                        <Marker key={`bath-${bp.id}`} position={[bp.location.latitude, bp.location.longitude]}
                          eventHandlers={{ click: () => focusListItem('bathing', `bath-${bp.id}`, idx) }}
                          icon={L.divIcon({ className: '', html: `<div style="position:relative;width:30px;height:30px;border-radius:50%;background:${color};border:2px solid rgba(255,255,255,0.7);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.3);color:white;"><i class="fa-solid fa-person-swimming" style="font-size:14px;"></i>${tempBadge}</div>`, iconSize: [30, 30], iconAnchor: [15, 15] })} />
                      );
                    })}
                  </MarkerClusterGroup>
                )}

                {/* Wild spots cluster (AI-found from Firebase) */}
                {sortedWildSpots.length > 0 && (
                  <MarkerClusterGroup chunkedLoading maxClusterRadius={45} showCoverageOnHover={false}
                    iconCreateFunction={(cluster: any) => createCustomMarker('fa-solid fa-tent', '#2E7D32', false, false, undefined, cluster.getChildCount())}>
                    {sortedWildSpots.map((spot, idx) => (
                      <Marker key={`wildspot-${spot.id}`} position={[spot.location.latitude, spot.location.longitude]}
                        eventHandlers={{ click: () => focusListItem('camping', `wildspot-${spot.id}`, idx) }}
                        icon={createSpotIcon(spot, false)} />
                    ))}
                  </MarkerClusterGroup>
                )}

                {/* Route waypoints */}
                {route?.routeWaypoints?.map((wp, i) => {
                  const meta = getWaypointMeta(wp.icon);
                  return (
                    <Marker
                      key={`wp-${i}`}
                      position={[wp.latitude, wp.longitude]}
                      icon={L.divIcon({
                        className: '',
                        html: `<div style="display:flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;background:${meta.defaultColor}dd;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4);font-size:14px;">${meta.emoji}</div>`,
                        iconSize: [28, 28],
                        iconAnchor: [14, 14],
                      })}
                    >
                      <Popup>
                        <div style={{ minWidth: 120, fontFamily: 'system-ui, sans-serif', color: '#1c1917' }}>
                          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>{meta.emoji} {wp.name}</div>
                          {wp.description && <div style={{ fontSize: 11, color: '#78716c' }}>{wp.description}</div>}
                        </div>
                      </Popup>
                    </Marker>
                  );
                })}
              </MapContainer>
            ) : (
              <div className="text-[var(--text-muted)] flex flex-col items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-2xs font-bold uppercase tracking-widest">{t('shared.loading_map')}</span>
              </div>
            )}

            {/* Map overlay badges */}
            <div className="absolute top-4 left-4 z-[1000] flex flex-wrap gap-2 max-w-[60%]">
              <span className="px-3 py-1.5 rounded-xl bg-black/85 border border-white/15 text-2xs font-black uppercase tracking-wider text-white truncate max-w-[200px]">
                {displayName}
              </span>
              {route.distance && (
                <span className="px-3 py-1.5 rounded-xl bg-black/85 border border-emerald-500/30 text-2xs font-black uppercase tracking-wider text-emerald-300">
                  {route.distance.toFixed(1)} km
                </span>
              )}
            </div>

            {/* Map type switch */}
            <div className="absolute top-4 right-4 z-[1000]">
              <button
                onClick={() => setMapType(prev => prev === 'topo' ? 'satellite' : prev === 'satellite' ? 'dark' : 'topo')}
                className="w-10 h-10 rounded-xl bg-[var(--bg-surface)] backdrop-blur-md border border-[var(--border-secondary)] flex items-center justify-center text-[var(--text-primary)] hover:bg-emerald-500 transition-colors shadow-xl"
              >
                {mapType === 'topo' ? <Layers size={18} /> : mapType === 'satellite' ? <MapIcon size={18} /> : <Layers size={18} />}
              </button>
            </div>
          </div>

          {/* 3D Flyover modal */}
          {showFlyover && route && (
            <Suspense fallback={<div className="fixed inset-0 z-modal bg-black/80 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>}>
              <Route3DFlyover route={route} onClose={() => setShowFlyover(false)} />
            </Suspense>
          )}

          <div className="pt-6 space-y-6">
            {/* 5. Description */}
            <div className="bg-[var(--bg-surface)] backdrop-blur-md p-5 md:p-6 rounded-2xl border border-[var(--border-primary)] animate-in fade-in slide-in-from-bottom-4 duration-700">
              <h3 className="text-tiny font-black text-emerald-500 uppercase tracking-[0.2em] mb-3">{t('shared.about_route')}</h3>
              <p className="text-sm md:text-base text-[var(--text-primary)] leading-relaxed font-medium">{displayDescription}</p>
            </div>

            {/* 6. Elevation Profile */}
            {route.elevationProfile && route.elevationProfile.length > 0 && route.elevationProfile.some(p => p.elevation > 0) && (
              <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:50ms]">
                <h3 className="text-tiny font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t('shared.elevation_profile')}</h3>
                <ElevationProfile
                  profile={route.elevationProfile}
                  markers={elevationMarkers}
                  onMarkerClick={handleElevationMarkerClick}
                  startLabel={startLabel}
                  endLabel={endLabel}
                />
                {elevationMarkers.length > 0 && (
                  <div className="flex flex-wrap gap-3 px-1">
                    {sortedWildSpots.length > 0 && (
                      <span className="flex items-center gap-1 text-3xs text-[var(--text-muted)]">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" />
                        {t('shared.tab_wildspots')}
                      </span>
                    )}
                    {fetchedWikiSpots.length > 0 && (
                      <span className="flex items-center gap-1 text-3xs text-[var(--text-muted)]">
                        <span className="w-2 h-2 rounded-full bg-violet-500 inline-block" />
                        {t('shared.tab_attractions')}
                      </span>
                    )}
                    {restAreas.length > 0 && (
                      <span className="flex items-center gap-1 text-3xs text-[var(--text-muted)]">
                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                        {t('shared.tab_rest')}
                      </span>
                    )}
                    {chargers.length > 0 && (
                      <span className="flex items-center gap-1 text-3xs text-[var(--text-muted)]">
                        <span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" />
                        {t('shared.tab_chargers')}
                      </span>
                    )}
                    {bathingPlaces.length > 0 && (
                      <span className="flex items-center gap-1 text-3xs text-[var(--text-muted)]">
                        <span className="w-2 h-2 rounded-full bg-cyan-500 inline-block" />
                        {t('shared.tab_bathing')}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 7. Facilities Stat Cards */}
            {route.stats && (
              <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:50ms]">
                <h3 className="text-tiny font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t('shared.facilities_along')}</h3>
                <div className={`grid grid-cols-2 ${facilityCounts.chargers > 0 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'} gap-2`}>
                  <StatCard icon={<Tent size={14} />} count={facilityCounts.wildSpots} label={t('shared.stat_wildcamping')} color="emerald" />
                  <StatCard icon={<Coffee size={14} />} count={facilityCounts.restAreas} label={t('shared.stat_rest_areas')} color="blue" />
                  <StatCard icon={<Mountain size={14} />} count={facilityCounts.campsites} label={t('shared.stat_camping')} color="indigo" />
                  <StatCard icon={<Landmark size={14} />} count={facilityCounts.attractions} label={t('shared.stat_attractions')} color="violet" />
                  {facilityCounts.chargers > 0 && <StatCard icon={<Zap size={14} />} count={facilityCounts.chargers} label={t('shared.stat_chargers')} color="yellow" />}
                  {facilityCounts.bathing > 0 && <StatCard icon={<Waves size={14} />} count={facilityCounts.bathing} label={t('shared.stat_bathing')} color="cyan" />}
                </div>
              </div>
            )}

            {/* 7b. Points Along Route — Tab Container */}
            <div ref={tabContainerRef} className="bg-[var(--bg-surface)] backdrop-blur-md rounded-2xl border border-[var(--border-primary)] p-4 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:100ms]">
              <h3 className="text-tiny font-black text-[var(--text-muted)] uppercase tracking-widest mb-3">{t('shared.points_along')}</h3>
              {facilitiesLoading && (
                <div className="flex items-center gap-2 mb-3">
                  <Loader2 size={12} className="animate-spin text-emerald-500" />
                  <span className="text-2xs text-[var(--text-secondary)]">{t('shared.loading_facilities')}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-1.5 mb-4">
                {([
                  { key: 'camping' as const, label: t('shared.tab_wildspots'), count: facilityCounts.wildSpots, Icon: Tent, active: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40', deferred: false },
                  { key: 'external_camping' as const, label: t('shared.tab_camping'), count: facilityCounts.campsites, Icon: Mountain, active: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40', deferred: false },
                  { key: 'rest' as const, label: t('shared.tab_rest'), count: facilityCounts.restAreas, Icon: Coffee, active: 'bg-blue-500/20 text-blue-300 border-blue-500/40', deferred: true },
                  { key: 'chargers' as const, label: t('shared.tab_chargers'), count: facilityCounts.chargers, Icon: Zap, active: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40', deferred: true },
                  { key: 'attractions' as const, label: t('shared.tab_attractions'), count: facilityCounts.attractions, Icon: Landmark, active: 'bg-violet-500/20 text-violet-300 border-violet-500/40', deferred: persistedWikiSpots.length === 0 },
                  { key: 'wildlife' as const, label: t('shared.tab_wildlife'), count: facilityCounts.wildlife, Icon: Bird, active: 'bg-purple-500/20 text-purple-300 border-purple-500/40', deferred: true },
                  { key: 'amenities' as const, label: t('shared.tab_amenities'), count: facilityCounts.amenities, Icon: MapPinned, active: 'bg-teal-500/20 text-teal-300 border-teal-500/40', deferred: false },
                  { key: 'bathing' as const, label: t('shared.tab_bathing'), count: facilityCounts.bathing, Icon: Waves, active: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40', deferred: true },
                ]).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => { setActiveInfoTab(tab.key); setTabPage(p => ({ ...p, [tab.key]: 0 })); }}
                    className={`px-2.5 py-1.5 rounded-xl text-3xs font-black uppercase tracking-widest border transition-colors ${activeInfoTab === tab.key ? tab.active : 'bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border-[var(--border-secondary)]'}`}
                  >
                    <tab.Icon size={10} className="inline mr-1 -mt-0.5" />
                    {tab.label} {deferredLoading.has(tab.key) ? <Loader2 size={8} className="inline animate-spin ml-0.5" /> : !tab.deferred || loadedTabsRef.current.has(tab.key) ? `(${tab.count})` : ''}
                  </button>
                ))}
              </div>

              {/* Pagination helper */}
              {(() => {
                const PAGE_SIZE = 20;
                const page = tabPage[activeInfoTab] || 0;
                const setPage = (p: number) => setTabPage(prev => ({ ...prev, [activeInfoTab]: p }));

                const PaginationBar: React.FC<{ total: number }> = ({ total }) => {
                  const totalPages = Math.ceil(total / PAGE_SIZE);
                  if (totalPages <= 1) return null;
                  return (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--border-primary)]">
                      <button
                        onClick={() => setPage(Math.max(0, page - 1))}
                        disabled={page === 0}
                        className="px-3 py-1.5 rounded-lg text-2xs font-bold border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[var(--bg-surface)] transition-colors"
                      >
                        <ChevronLeft size={12} className="inline -mt-0.5" /> {t('shared.previous')}
                      </button>
                      <span className="text-2xs text-[var(--text-muted)] font-bold">{page + 1} / {totalPages}</span>
                      <button
                        onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                        disabled={page >= totalPages - 1}
                        className="px-3 py-1.5 rounded-lg text-2xs font-bold border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[var(--bg-surface)] transition-colors"
                      >
                        {t('shared.next')} <ChevronRight size={12} className="inline -mt-0.5" />
                      </button>
                    </div>
                  );
                };

                const paginated = <T,>(items: T[]) => items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

                const NavButton: React.FC<{ lat: number; lon: number; name?: string; spotId?: string }> = ({ lat, lon, name, spotId }) => {
                  const ua = navigator.userAgent;
                  const isTesla = /Tesla/.test(ua);

                  if (isTesla) {
                    return (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setTeslaQrData({ lat, lon, name, spotId });
                        }}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 text-2xs font-bold hover:bg-emerald-500/25 transition-colors"
                      >
                        <Navigation size={11} /> {t('shared.navigate_here')}
                      </button>
                    );
                  }

                  return (
                    <a
                      href={`https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&travelmode=driving`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 text-2xs font-bold hover:bg-emerald-500/25 transition-colors"
                      onClick={e => e.stopPropagation()}
                    >
                      <Navigation size={11} /> {t('shared.navigate_here')}
                    </a>
                  );
                };

                const connectorIcon = (conn: string) => {
                  const c = String(conn || '').toLowerCase();
                  if (c.includes('ccs')) return '⚡';
                  if (c.includes('chademo')) return '🔌';
                  if (c.includes('type 2')) return '🔋';
                  if (c.includes('schuko')) return '🏠';
                  return '🔌';
                };

                return (
                  <>
                    {/* Fricamp/Wild camping tab */}
                    {activeInfoTab === 'camping' && (() => {
                      const getSpotTypeInfo = (spot: CampingSpot) => {
                        const feat = (spot.features || []).join(' ').toUpperCase();
                        if (feat.includes('BÅLPLASS') || feat.includes('FIREPIT')) return { label: t('shared.firepit'), color: '#f97316', icon: 'fa-solid fa-fire' };
                        if (feat.includes('GAPAHUK') || feat.includes('SHELTER')) return { label: t('shared.shelter'), color: BRAND, icon: 'fa-solid fa-person-shelter' };
                        if (feat.includes('UTSIKT') || feat.includes('VIEWPOINT')) return { label: t('shared.viewpoint'), color: BRAND, icon: 'fa-solid fa-mountain' };
                        if (feat.includes('PICNIC')) return { label: t('shared.picnic'), color: '#3b82f6', icon: 'fa-solid fa-utensils' };
                        if (feat.includes('HYTTE') || feat.includes('CABIN')) return { label: t('shared.cabin'), color: '#a855f7', icon: 'fa-solid fa-house' };
                        if (feat.includes('BADEPLASS')) return { label: t('shared.swimming'), color: '#0ea5e9', icon: 'fa-solid fa-person-swimming' };
                        const score = isNaN(spot.score) ? 0 : Math.round(spot.score);
                        const sc = score >= 8 ? '#2E7D32' : score >= 4 ? '#f59e0b' : '#78716c';
                        return { label: t('shared.wild_spot'), color: sc, icon: 'fa-solid fa-tent' };
                      };
                      const translateFeature = (f: string) => {
                        const u = f.toUpperCase();
                        if (u.includes('BÅLPLASS') || u.includes('FIREPIT')) return t('shared.firepit');
                        if (u.includes('UTSIKT') || u.includes('VIEWPOINT')) return t('shared.viewpoint');
                        if (u.includes('GAPAHUK') || u.includes('SHELTER')) return t('shared.shelter');
                        if (u.includes('HYTTE') || u.includes('CABIN')) return t('shared.cabin');
                        if (u.includes('RASTEPLASS') || u.includes('PICNIC')) return t('shared.picnic');
                        if (u.includes('BADEPLASS')) return t('shared.swimming');
                        if (u.includes('TOALETT')) return t('shared.toilet');
                        if (u.includes('VANN') || u.includes('DRIKKEVANN')) return t('shared.water');
                        if (u.includes('VEI-ENDE') || u.includes('ROAD END')) return t('spot.categories.road_end');
                        if (u.includes('SKOGSVEI') || u.includes('FOREST ROAD')) return t('spot.categories.forest_road');
                        if (u.includes('SNUPLASS') || u.includes('TURNING')) return t('spot.categories.turning_circle');
                        if (u.includes('PARKERING') || u === 'PARKING') return t('spot.categories.parking');
                        if (u.includes('VILLMARK') || u.includes('WILDERNESS') || u.includes('UTMARK')) return t('spot.wilderness');
                        // Handle raw i18n keys (e.g. "spot.wilderness", "spot.categories.road_end")
                        if (f.includes('.') && !f.includes(' ')) {
                          const translated = t(f);
                          if (translated !== f) return translated;
                        }
                        return f;
                      };
                      return (
                        <div className="space-y-2">
                          {sortedWildSpots.length === 0 && (
                            <div className="border border-emerald-500/20 rounded-2xl overflow-hidden bg-gradient-to-b from-emerald-500/5 to-transparent p-4 space-y-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl shrink-0 bg-emerald-600 flex items-center justify-center">
                                  <i className="fa-solid fa-tent text-white text-sm" />
                                </div>
                                <div>
                                  <span className="text-sm font-black text-[var(--text-primary)] block">Fricamp</span>
                                  {route.stats?.wildSpotsCount ? (
                                    <span className="text-2xs text-emerald-300">{t('shared.wildspots_count', { count: route.stats.wildSpotsCount })}</span>
                                  ) : (
                                    <span className="text-2xs text-[var(--text-secondary)]">{t('shared.empty_wildspots')}</span>
                                  )}
                                </div>
                              </div>
                              <p className="text-tiny text-[var(--text-secondary)] leading-relaxed">
                                {t('shared.wildspots_in_app', { count: route.stats?.wildSpotsCount || 0 })}
                              </p>
                              <a href={withLang(`/app/view/route/${routeId}`)} target="_blank" rel="noopener noreferrer"
                                className="w-full h-11 rounded-xl bg-emerald-600 text-white font-black text-2xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-emerald-500 transition-colors">
                                <ExternalLink size={12} /> {t('shared.explore_in_app')}
                              </a>
                            </div>
                          )}
                          {paginated(sortedWildSpots).map((spot, idx) => {
                            const id = `wildspot-${spot.id}`;
                            const expanded = expandedIds.has(id);
                            const typeInfo = getSpotTypeInfo(spot);
                            const score = isNaN(spot.score) ? 0 : Math.round(spot.score);
                            const scoreColor = score >= 8 ? 'text-emerald-400 bg-emerald-500/15 border-emerald-500/30' : score >= 4 ? 'text-amber-400 bg-amber-500/15 border-amber-500/30' : 'text-[var(--text-secondary)] bg-[var(--bg-surface-alt)] border-[var(--border-secondary)]';
                            const isFirst = idx === 0 && (tabPage['camping'] || 0) === 0;
                            const validFeatures = (spot.features || []).filter(f => !['OSM', 'NVDB', 'SØKERESULTAT'].includes(f.toUpperCase()));

                            // First spot: rich card like the main app
                            if (isFirst) {
                              return (
                                <div key={`tab-${id}`} className="space-y-2">
                                  <SpotCard
                                    spot={spot}
                                    isSelected={true}
                                    activeHikingTrailId={null}
                                    isFavorite={false}
                                    isVisited={false}
                                    onSelect={() => { }}
                                    onSelectHikingTrail={() => { }}
                                    onToggleFavorite={handleOpenInAppAndFavorite}
                                    user={null}
                                    onOpenAuth={() => { }}
                                    hideShareAction={true}
                                    hideNavigateAction={true}
                                    hideNoteAction={true}
                                    hideCreateRouteAction={true}
                                    hideReportAction={true}
                                  />
                                  <a
                                    href={withLang(`/app/view/route/${routeId}`)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full h-10 rounded-xl bg-emerald-600 text-white font-black text-2xs uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-emerald-500 transition-colors"
                                  >
                                    <ExternalLink size={12} /> {t('shared.explore_in_app')}
                                  </a>
                                </div>
                              );
                            }

                            // Remaining spots: compact list with correct labels
                            return (
                              <div key={`tab-${id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                                <button onClick={() => toggleExpand(id)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left transition-colors">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center" style={{ background: `${typeInfo.color}33` }}>
                                      <i className={`${typeInfo.icon} text-sm`} style={{ color: typeInfo.color }} />
                                    </div>
                                    <div className="min-w-0">
                                      <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{spot.name}</span>
                                      <span className="text-2xs text-[var(--text-muted)]">{typeInfo.label}</span>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    {score > 0 && (
                                      <span className={`px-1.5 py-0.5 rounded-md border text-2xs font-black ${scoreColor}`}>
                                        {score}/10
                                      </span>
                                    )}
                                    {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                                  </div>
                                </button>
                                {expanded && (
                                  <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2.5 border-t border-[var(--border-primary)]">
                                    {spot.description && <p className="leading-relaxed">{spot.description}</p>}
                                    <div className="flex flex-wrap gap-1.5 pt-1">
                                      {validFeatures.slice(0, 4).map((f, i) => (
                                        <span key={i} className="px-2 py-0.5 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-2xs text-emerald-300 uppercase font-black">{translateFeature(f)}</span>
                                      ))}
                                    </div>
                                    <div className="flex gap-2">
                                      <a href={withLang(`/app/view/route/${routeId}`)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/25 text-emerald-300 text-2xs font-bold hover:bg-emerald-500/25 transition-colors">
                                        <ExternalLink size={10} /> {t('shared.explore_in_app')}
                                      </a>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <PaginationBar total={sortedWildSpots.length} />
                        </div>
                      );
                    })()}

                    {/* Commercial Camping tab */}
                    {activeInfoTab === 'external_camping' && (
                      <div className="space-y-2">
                        {campsites.length === 0 && <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared.empty_camping')}</div>}
                        {paginated(campsites).map((spot: CampingSpot) => {
                          const id = `camp-${spot.id}`;
                          const expanded = expandedIds.has(id);
                          const cMeta = spot.commercialMetadata;
                          return (
                            <div key={`tab-${id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                              <button onClick={() => toggleExpand(id)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left transition-colors">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-lg shrink-0 bg-[#1565C0]/20 border border-[#1565C0]/30 flex items-center justify-center">
                                    <Mountain size={14} className="text-[#1565C0]" />
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{spot.name}</span>
                                    {cMeta?.fee && <span className="text-2xs text-[var(--text-muted)]">{cMeta.fee}</span>}
                                  </div>
                                </div>
                                {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                              </button>
                              {expanded && (
                                <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2.5 border-t border-[var(--border-primary)]">
                                  {spot.description && <p className="leading-relaxed">{spot.description}</p>}
                                  {cMeta?.phone && (
                                    <a href={`tel:${cMeta.phone}`} onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-500/15 border border-blue-500/25 text-blue-300 text-2xs font-bold hover:bg-blue-500/25 transition-colors">
                                      <Phone size={11} /> {cMeta.phone}
                                    </a>
                                  )}
                                  {cMeta?.website && (
                                    <a href={cMeta.website.startsWith('http') ? cMeta.website : `https://${cMeta.website}`} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-violet-500/15 border border-violet-500/25 text-violet-300 text-2xs font-bold hover:bg-violet-500/25 transition-colors">
                                      <Globe size={11} /> {t('shared.website')}
                                    </a>
                                  )}
                                  <NavButton lat={spot.location.latitude} lon={spot.location.longitude} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <PaginationBar total={campsites.length} />
                      </div>
                    )}

                    {/* Rest areas tab */}
                    {activeInfoTab === 'rest' && (
                      <div className="space-y-2">
                        {restAreas.length === 0 && !facilitiesLoading && !deferredLoading.has('rest') && <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared.empty_rest')}</div>}
                        {restAreas.length === 0 && (facilitiesLoading || deferredLoading.has('rest')) && (
                          <div className="flex items-center gap-2 py-2">
                            <Loader2 size={12} className="animate-spin text-blue-400" />
                            <span className="text-tiny text-[var(--text-secondary)]">{t('shared.loading_facilities')}</span>
                          </div>
                        )}
                        {paginated(restAreas).map(ra => {
                          const id = `rest-${ra.id}`;
                          const expanded = expandedIds.has(id);
                          const info = ra.rasteplassInfo;
                          const facilityIcons = info ? [
                            info.toalett && { emoji: '🚻', label: t('shared.toilet'), color: 'blue' },
                            info.mobler && { emoji: '🪑', label: t('shared.bench'), color: 'blue' },
                            info.soppel && { emoji: '🗑️', label: t('shared.trash'), color: 'blue' },
                            info.belysning && { emoji: '💡', label: t('shared.lighting'), color: 'blue' },
                            info.vinterstengt && { emoji: '❄️', label: t('shared.winter_closed'), color: 'orange' },
                          ].filter(Boolean) as { emoji: string; label: string; color: string }[] : [];
                          const typeLabel = info?.type ? (() => {
                            const key = `shared.rest_type_${info.type.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, '')}`;
                            const translated = t(key, '');
                            return translated || info.type;
                          })() : null;
                          return (
                            <div key={`tab-${id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                              <button onClick={() => toggleExpand(id)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-start justify-between text-left transition-colors gap-2">
                                <div className="flex items-start gap-2.5 min-w-0 flex-1">
                                  <div className="w-8 h-8 rounded-lg shrink-0 bg-blue-500/20 border border-blue-500/30 flex items-center justify-center mt-0.5">
                                    <Coffee size={14} className="text-blue-400" />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{ra.name || t('shared.rest_area')}</span>
                                    {typeLabel && (
                                      <span className="text-2xs text-[var(--text-secondary)] block mt-0.5">{typeLabel}{info?.kapasitet && info.kapasitet > 0 ? ` · ${t('shared.capacity', { count: info.kapasitet })}` : ''}</span>
                                    )}
                                    {facilityIcons.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1.5">
                                        {facilityIcons.map(f => (
                                          <span key={f.label} className={`px-1.5 py-0.5 rounded-md text-2xs ${f.color === 'orange' ? 'bg-orange-500/10 border border-orange-500/20 text-orange-300' : 'bg-blue-500/10 border border-blue-500/20 text-blue-300'}`}>
                                            {f.emoji} {f.label}
                                          </span>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)] mt-1 shrink-0" /> : <ChevronDown size={14} className="text-[var(--text-secondary)] mt-1 shrink-0" />}
                              </button>
                              {expanded && (
                                <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2 border-t border-[var(--border-primary)]">
                                  <NavButton lat={ra.location.latitude} lon={ra.location.longitude} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <PaginationBar total={restAreas.length} />
                      </div>
                    )}

                    {/* Chargers tab */}
                    {activeInfoTab === 'chargers' && (
                      <div className="space-y-2">
                        {chargers.length === 0 && <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared.empty_chargers')}</div>}
                        {paginated(chargers).map(c => {
                          const id = `chg-${c.id}`;
                          const expanded = expandedIds.has(id);
                          return (
                            <div key={`tab-${id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                              <button onClick={() => toggleExpand(id)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left transition-colors">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-lg shrink-0 bg-yellow-500/20 border border-yellow-500/30 flex items-center justify-center">
                                    <Zap size={14} className="text-yellow-400" />
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{c.name}</span>
                                    <span className="text-2xs text-[var(--text-muted)]">{c.chargingPoints !== 1 ? t('shared.charging_points_plural', { count: c.chargingPoints }) : t('shared.charging_points', { count: c.chargingPoints })}{c.operator ? ` · ${c.operator}` : ''}</span>
                                  </div>
                                </div>
                                {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                              </button>
                              {expanded && (
                                <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2.5 border-t border-[var(--border-primary)]">
                                  {c.connectors?.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">{c.connectors.map((conn, i) => (
                                      <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-yellow-500/10 border border-yellow-500/20 text-2xs font-bold text-yellow-300">
                                        <Plug size={10} /> {connectorIcon(conn)} {conn}
                                      </span>
                                    ))}</div>
                                  )}
                                  {c.address && (
                                    <p className="text-2xs text-[var(--text-secondary)] flex items-center gap-1">
                                      <MapPinned size={10} className="shrink-0" /> {c.address}{c.city ? `, ${c.city}` : ''}
                                    </p>
                                  )}
                                  <NavButton lat={c.location.latitude} lon={c.location.longitude} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <PaginationBar total={chargers.length} />
                      </div>
                    )}

                    {/* Attractions tab */}
                    {activeInfoTab === 'attractions' && (
                      <div className="space-y-2">
                        {fetchedWikiSpots.length === 0 && <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared.empty_attractions')}</div>}
                        {paginated(fetchedWikiSpots).map(spot => {
                          const expanded = expandedIds.has(`wiki-${spot.pageid}`);
                          const ew = getEffectiveWiki(spot);
                          return (
                            <div key={`tab-wiki-${spot.pageid}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                              <button onClick={() => toggleExpand(`wiki-${spot.pageid}`)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left transition-colors">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  {(ew.thumbnail?.source || spot.thumbnail?.source) ? (
                                    <img src={ew.thumbnail?.source || spot.thumbnail!.source} alt="" className="w-8 h-8 rounded-lg shrink-0 object-cover border border-[var(--border-secondary)]" />
                                  ) : (
                                    <div className="w-8 h-8 rounded-lg shrink-0 bg-violet-500/20 border border-violet-500/30 flex items-center justify-center">
                                      <Landmark size={14} className="text-violet-400" />
                                    </div>
                                  )}
                                  <div className="min-w-0">
                                    <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{ew.title}</span>
                                    {ew.extract && <span className="text-2xs text-[var(--text-muted)] line-clamp-1">{ew.extract.slice(0, 80)}</span>}
                                  </div>
                                </div>
                                {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                              </button>
                              {expanded && (
                                <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2.5 border-t border-[var(--border-primary)]">
                                  {(ew.thumbnail?.source || spot.thumbnail?.source) && (
                                    <img src={ew.thumbnail?.source || spot.thumbnail!.source} alt={ew.title} className="w-full h-36 object-cover rounded-lg border border-[var(--border-secondary)]" />
                                  )}
                                  {ew.extract && <p className="leading-relaxed">{ew.extract}</p>}
                                  <div className="flex flex-wrap gap-2">
                                    <a href={ew.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-violet-500/15 border border-violet-500/25 text-violet-300 text-2xs font-bold hover:bg-violet-500/25 transition-colors">
                                      <Globe size={11} /> {t('shared.read_more_wiki')}
                                    </a>
                                    <NavButton lat={spot.location.latitude} lon={spot.location.longitude} />
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <PaginationBar total={fetchedWikiSpots.length} />
                      </div>
                    )}

                    {/* Wildlife tab */}
                    {activeInfoTab === 'wildlife' && (
                      <div className="space-y-2">
                        {wildlife.length === 0 && !facilitiesLoading && <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared.empty_wildlife')}</div>}
                        {paginated(wildlife).map(w => {
                          const id = `wild-${w.id}`;
                          const expanded = expandedIds.has(id);
                          const dateStr = w.observedAt ? new Date(w.observedAt).toLocaleDateString(getUiLocale(), { day: 'numeric', month: 'short', year: 'numeric' }) : null;
                          return (
                            <div key={`tab-${id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                              <button onClick={() => toggleExpand(id)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left transition-colors">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-lg shrink-0 bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-lg">
                                    {w.emoji}
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{t(`wildlife.${w.species}.name`, w.species)}</span>
                                    <span className="text-2xs text-[var(--text-muted)]">
                                      {w.category === 'Mammal' ? t('shared.mammal') : t('shared.bird')}
                                      {dateStr ? ` · ${dateStr}` : ''}
                                    </span>
                                  </div>
                                </div>
                                {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                              </button>
                              {expanded && (
                                <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2 border-t border-[var(--border-primary)]">
                                  {w.thumbnailUrl && (
                                    <img src={w.thumbnailUrl} alt={t(`wildlife.${w.species}.name`, w.species)} className="w-full h-32 object-cover rounded-lg border border-[var(--border-secondary)]" />
                                  )}
                                  {dateStr && (
                                    <p className="text-2xs text-[var(--text-secondary)] flex items-center gap-1">
                                      <Calendar size={10} className="shrink-0" /> {t('shared.observed')}: {dateStr}
                                    </p>
                                  )}
                                  {w.locality && (
                                    <p className="text-2xs text-[var(--text-secondary)] flex items-center gap-1">
                                      <MapPinned size={10} className="shrink-0" /> {w.locality}{w.municipality ? `, ${w.municipality}` : ''}
                                    </p>
                                  )}
                                  <NavButton lat={w.location.latitude} lon={w.location.longitude} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <PaginationBar total={wildlife.length} />
                      </div>
                    )}

                    {/* Amenities tab (bålplass, gapahuk, utsikt, etc. from Overpass) */}
                    {activeInfoTab === 'amenities' && (
                      <div className="space-y-2">
                        {amenities.length === 0 && <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared.empty_amenities')}</div>}
                        {paginated(amenities).map(a => {
                          const id = `amen-${a.id}`;
                          const expanded = expandedIds.has(id);
                          const feat = (a.features || []).join(' ').toUpperCase();
                          const isFirepit = feat.includes('BÅLPLASS') || feat.includes('FIREPIT');
                          const isViewpoint = feat.includes('UTSIKT') || feat.includes('VIEWPOINT');
                          const isShelter = feat.includes('GAPAHUK') || feat.includes('SHELTER');
                          const typeLabel = isFirepit ? t('shared.firepit') : isViewpoint ? t('shared.viewpoint') : isShelter ? t('shared.shelter') : feat.includes('HYTTE') ? t('shared.cabin') : t('shared.picnic');
                          const typeColor = isFirepit ? '#f97316' : isViewpoint ? '#0ea5e9' : isShelter ? '#0d9488' : '#3b82f6';
                          const typeIcon = isFirepit ? 'fa-solid fa-fire' : isViewpoint ? 'fa-solid fa-mountain' : isShelter ? 'fa-solid fa-person-shelter' : 'fa-solid fa-utensils';
                          return (
                            <div key={`tab-${id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                              <button onClick={() => toggleExpand(id)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left transition-colors">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-lg shrink-0 flex items-center justify-center" style={{ background: `${typeColor}33` }}>
                                    <i className={`${typeIcon} text-sm`} style={{ color: typeColor }} />
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{a.name}</span>
                                    <span className="text-2xs text-[var(--text-muted)]">{typeLabel}</span>
                                  </div>
                                </div>
                                {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                              </button>
                              {expanded && (
                                <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2 border-t border-[var(--border-primary)]">
                                  {a.description && <p className="leading-relaxed">{a.description}</p>}
                                  <p className="text-2xs text-[var(--text-secondary)] flex items-center gap-1">
                                    <MapPinned size={10} className="shrink-0" /> {a.location.latitude.toFixed(4)}, {a.location.longitude.toFixed(4)}
                                  </p>
                                  <NavButton lat={a.location.latitude} lon={a.location.longitude} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <PaginationBar total={amenities.length} />
                      </div>
                    )}

                    {/* Bathing tab */}
                    {activeInfoTab === 'bathing' && (
                      <div className="space-y-2">
                        {bathingPlaces.length === 0 && <div className="text-tiny text-[var(--text-secondary)] italic">{t('shared.empty_bathing')}</div>}
                        {paginated(bathingPlaces).map(bp => {
                          const id = `bath-${bp.id}`;
                          const expanded = expandedIds.has(id);
                          const hasWarning = bp.waterQualityWarning || false;
                          const hasTemp = bp.waterTemperature != null && bp.waterTemperature > 0;
                          return (
                            <div key={`tab-${id}`} className="border border-[var(--border-secondary)] rounded-xl overflow-hidden">
                              <button onClick={() => toggleExpand(id)} className="w-full px-3 py-2.5 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] flex items-center justify-between text-left transition-colors">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center ${hasWarning ? 'bg-red-500/20 border border-red-500/30' : 'bg-cyan-500/20 border border-cyan-500/30'}`}>
                                    <i className="fa-solid fa-person-swimming text-sm" style={{ color: hasWarning ? '#ef4444' : '#0ea5e9' }} />
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-sm font-bold text-[var(--text-primary)] truncate block">{bp.name}</span>
                                    <span className="text-2xs text-[var(--text-muted)]">
                                      {bp.municipality || t('map.bathing.title')}
                                      {hasTemp && <span className="ml-1.5">🌡 {bp.waterTemperature!.toFixed(1)}°C</span>}
                                    </span>
                                  </div>
                                </div>
                                {expanded ? <ChevronUp size={14} className="text-[var(--text-secondary)]" /> : <ChevronDown size={14} className="text-[var(--text-secondary)]" />}
                              </button>
                              {expanded && (
                                <div className="px-3 py-2.5 text-[12px] text-[var(--text-secondary)] space-y-2 border-t border-[var(--border-primary)]">
                                  {hasWarning && (
                                    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 flex items-start gap-2">
                                      <i className="fa-solid fa-triangle-exclamation text-red-400 mt-0.5" style={{ fontSize: 12 }} />
                                      <span className="text-2xs text-red-300 font-semibold">{t('map.bathing.water_quality_warning')}</span>
                                    </div>
                                  )}
                                  {bp.description && <p className="leading-relaxed text-tiny">{bp.description}</p>}
                                  <p className="text-2xs text-[var(--text-muted)]">
                                    {t(`map.bathing.source_${bp.source}`)}
                                  </p>
                                  <NavButton lat={bp.location.latitude} lon={bp.location.longitude} />
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <PaginationBar total={bathingPlaces.length} />
                      </div>
                    )}

                    {/* Deferred tab loading indicator */}
                    {deferredLoading.has(activeInfoTab) && (
                      <div className="flex items-center gap-2 py-4 justify-center">
                        <Loader2 size={14} className="animate-spin text-emerald-500" />
                        <span className="text-2xs text-[var(--text-secondary)]">{t('shared.loading_facilities')}</span>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            {/* 8. Weather Section */}
            {(weatherLoading || weather7d.length > 0) && (
              <div className="bg-[var(--bg-surface)] backdrop-blur-md rounded-2xl border border-[var(--border-primary)] p-4 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:100ms]">
                <h3 className="text-2xs font-black uppercase tracking-widest text-emerald-400/80 mb-3 flex items-center gap-2">
                  <CloudSun size={12} /> {t('shared.weather_title')}
                </h3>
                {weatherLoading && <p className="text-tiny text-[var(--text-secondary)] italic">{t('shared.loading_weather')}</p>}
                {weather7d.length > 0 && (
                  <div className="grid grid-cols-7 gap-1">
                    {weather7d.map(day => (
                      <div key={day.key} className="min-w-0 rounded-xl border border-white/15 bg-gradient-to-b from-emerald-500/18 to-sky-900/22 px-2 py-2 shadow-lg">
                        <div className="text-3xs font-black uppercase tracking-tight text-[var(--text-primary)] truncate">{day.label === '__today__' ? t('shared.today') : day.label === '__tomorrow__' ? t('shared.tomorrow') : day.label}</div>
                        <div className="mt-1.5 flex items-center justify-center"><WeatherSymbolIcon symbol={day.symbol} size={22} /></div>
                        <div className="mt-1 text-center">
                          <div className="text-[13px] font-black text-[var(--text-primary)] leading-none">{day.maxTemp}°</div>
                          <div className="text-2xs font-bold text-sky-200/85 leading-none mt-0.5">{day.minTemp}°</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {sunData && (
                  <div className="mt-3 rounded-xl border border-[var(--border-secondary)] bg-[var(--bg-surface-alt)] px-3 py-2 flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 text-tiny text-[var(--text-secondary)]"><Sunrise size={13} className="text-amber-400" /> {sunData.sunrise}</div>
                    <div className="text-2xs font-black uppercase tracking-widest text-amber-300">{t('shared.daylight_hours', { count: sunData.daylightHours })}</div>
                    <div className="flex items-center gap-1.5 text-tiny text-[var(--text-secondary)]"><Sunset size={13} className="text-orange-400" /> {sunData.sunset}</div>
                    {snowData && snowData.depthCm > 0 && (
                      <div className="flex items-center gap-2 border-l border-[var(--border-secondary)] pl-2">
                        <div className="inline-flex items-center gap-1.5 rounded-lg border border-sky-300/35 bg-sky-400/15 px-2 py-1">
                          <CloudSnow size={13} className="text-sky-100" />
                          <span className="text-3xs font-black uppercase tracking-wider text-sky-100">{t('weather.snow')}</span>
                          <span className="text-[12px] font-black text-white">
                            {Math.round(snowData.depthCm)} {t('weather.snow_unit')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 9. External Links */}
            {route.externalLinks && route.externalLinks.length > 0 && (
              <div className="space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:150ms]">
                <h3 className="text-tiny font-black text-[var(--text-muted)] uppercase tracking-widest ml-1">{t('shared.links_info')}</h3>
                {route.externalLinks.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 bg-[var(--bg-surface-alt)] border border-[var(--border-secondary)] rounded-2xl hover:bg-[var(--bg-surface)] transition-colors">
                    <div className="p-2 bg-[var(--bg-surface-alt)] rounded-xl shrink-0">
                      {link.type === 'video' ? <span className="text-red-400 text-sm">▶</span> : link.type === 'image' ? <ImageIcon size={14} className="text-blue-400" /> : <ExternalLink size={14} className="text-blue-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-[var(--text-primary)] truncate">{link.title}</p>
                      <p className="text-3xs text-[var(--text-muted)] truncate">{link.url}</p>
                    </div>
                    <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />
                  </a>
                ))}
              </div>
            )}

            {/* (Spots + Wiki spots are now in the tab container above) */}

            {/* 12. Reviews Section (read-only) */}
            <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700 [animation-delay:200ms]">
              <h3 className="text-tiny font-black text-[var(--text-muted)] uppercase tracking-widest ml-1 flex items-center gap-2">
                <Star size={12} /> {t('shared.reviews')} {reviews.length > 0 && `(${reviews.length})`}
              </h3>
              {reviews.length === 0 && (
                <div className="bg-[var(--bg-surface-alt)] p-5 rounded-2xl border border-[var(--border-secondary)] text-center">
                  <p className="text-sm text-[var(--text-secondary)]">{t('shared.no_reviews')}</p>
                  <p className="text-2xs text-[var(--text-muted)] mt-1">{t('shared.open_app_review')}</p>
                </div>
              )}
              {reviews.length > 0 && (
                <div className="space-y-3">
                  {/* Average rating */}
                  <div className="bg-[var(--bg-surface-alt)] p-4 rounded-2xl border border-[var(--border-secondary)] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={18} fill={s <= Math.round(avgRating) ? '#fbbf24' : 'none'} className={s <= Math.round(avgRating) ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
                      ))}
                    </div>
                    <span className="text-sm font-black text-[var(--text-primary)]">{avgRating.toFixed(1)} <span className="text-[var(--text-muted)] font-bold text-xs">/ 5</span></span>
                  </div>
                  {/* Individual reviews */}
                  {reviews.map((review) => {
                    const createdMs = normalizeTimestampMs(review.createdAt);
                    const dateStr = Number.isFinite(createdMs) ? new Date(createdMs).toLocaleDateString(getUiLocale()) : '';
                    return (
                      <div key={review.id} className="bg-[var(--bg-surface-alt)] p-4 rounded-2xl border border-[var(--border-secondary)] space-y-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full overflow-hidden border border-[var(--border-secondary)] bg-[var(--bg-input)] flex items-center justify-center shrink-0">
                              {review.userPhoto ? (
                                <img src={review.userPhoto} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-xs font-black text-emerald-400">{(review.userName || 'A').charAt(0)}</span>
                              )}
                            </div>
                            <div>
                              <p className="text-xs font-bold text-[var(--text-primary)]">{review.userName || t('route.anonymous')}</p>
                              {dateStr && <p className="text-3xs text-[var(--text-muted)]">{dateStr}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-0.5">
                            {[1, 2, 3, 4, 5].map(s => (
                              <Star key={s} size={12} fill={s <= review.rating ? '#fbbf24' : 'none'} className={s <= review.rating ? 'text-amber-400' : 'text-[var(--text-muted)]'} />
                            ))}
                          </div>
                        </div>
                        {review.comment && <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{review.comment}</p>}
                        {review.photoUrl && (
                          <img src={review.photoUrl} alt={t('shared.reviews')} className="w-full h-40 object-cover rounded-xl border border-[var(--border-secondary)]" />
                        )}
                        {(review as any).videoUrl && (
                          <a href={(review as any).videoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--bg-surface-alt)] border border-white/15 text-[var(--text-primary)] hover:bg-[var(--bg-surface)] transition-colors text-2xs font-black uppercase tracking-widest">
                            <ExternalLink size={13} /> {t('shared.video')}
                          </a>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 13. CTA Buttons */}
            <div className="pt-6 space-y-3">
              <button
                onClick={() => window.location.assign(withLang(`/app/view/route/${route.id}`))}
                className="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black uppercase tracking-widest transition-all active:scale-[0.98] shadow-[0_8px_20px_rgba(16,185,129,0.25)] flex items-center justify-center gap-3 group"
              >
                {t('shared.explore_in_app')}
                <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
              </button>
              {/* Legal guide removed from public sharing pages */}
            </div>
          </div>

          {/* 14. Footer */}
          <footer className="mt-auto py-12 px-6 flex flex-col items-center gap-4 text-center">
            <div className="flex items-center gap-2 opacity-40">
              <div className="h-[1px] w-12 bg-white" />
              <img src="/Fricamplogo.png" className="h-6 w-6 grayscale" alt="" />
              <div className="h-[1px] w-12 bg-white" />
            </div>
            <p className="text-2xs text-[var(--text-muted)] max-w-xs leading-relaxed">
              {t('shared.footer_text')}
            </p>
            <a href="https://www.takleconsulting.com" target="_blank" rel="noopener noreferrer" className="text-2xs text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
              {t('shared.made_by')}
            </a>
          </footer>
        </div>

        {/* 15. Floating Mobile CTA Bar */}
        <div className="md:hidden fixed bottom-6 left-6 right-6 z-mapOverlay animate-in slide-in-from-bottom-8 duration-1000">
          <div className="grid grid-cols-2 gap-3">
            {coordinates.length > 0 ? (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${coordinates[0][0]},${coordinates[0][1]}&travelmode=driving`}
                target="_blank"
                rel="noopener noreferrer"
                className="h-14 rounded-2xl bg-orange-500 text-white font-black text-tiny uppercase tracking-[0.12em] shadow-2xl flex items-center justify-center gap-2 active:scale-95 transition-all"
              >
                <MapPin size={14} /> {t('shared.navigate_to_start')}
              </a>
            ) : (
              <div className="h-14 rounded-2xl bg-black/50 text-[var(--text-secondary)] font-black text-tiny uppercase tracking-[0.12em] flex items-center justify-center">
                {t('shared.navigate_to_start')}
              </div>
            )}
            <a
              href={withLang(`/app/view/route/${routeId}`)}
              className="h-14 rounded-2xl bg-emerald-600 text-white font-black text-tiny uppercase tracking-[0.12em] shadow-2xl flex items-center justify-center gap-2 active:scale-95 transition-all"
            >
              {t('shared.explore_app')}
            </a>
          </div>
        </div>

      </div>{/* close inner wrapper */}

      {/* Legal guide removed from public sharing pages */}

      {/* Tesla QR Modal */}
      {teslaQrData && (
        <div className="fixed inset-0 z-modal flex items-center justify-center p-4 animate-in fade-in duration-300">
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" onClick={() => setTeslaQrData(null)} />
          <div className="relative w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border-secondary)] rounded-modal p-8 flex flex-col items-center gap-6 shadow-3xl text-center">
            <button
              onClick={() => setTeslaQrData(null)}
              className="absolute top-6 right-6 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
            >
              <X size={24} />
            </button>

            <div className="w-16 h-16 bg-emerald-500/10 rounded-2xl flex items-center justify-center text-emerald-400 mb-2">
              <Navigation size={32} />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-[var(--text-primary)] tracking-tight uppercase">
                {t('tesla.nav_title')}
              </h3>
              <p className="text-xs text-[var(--text-secondary)] font-bold uppercase tracking-widest text-emerald-500">
                {t('tesla.scan_qr')}
              </p>
            </div>

            <div className="bg-white p-4 rounded-3xl shadow-2xl">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
                  teslaQrData.spotId
                    ? `https://app.fricamp.com/share/spot/${teslaQrData.spotId}`
                    : `https://www.google.com/maps/search/?api=1&query=${teslaQrData.lat},${teslaQrData.lon}`
                )}`}
                alt="QR Code"
                className="w-48 h-48"
              />
            </div>

            <p className="text-tiny text-[var(--text-secondary)] leading-relaxed max-w-[280px]">
              {t('tesla.instruction')}
            </p>

            <button
              onClick={() => setTeslaQrData(null)}
              className="w-full py-4 bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-primary)] rounded-2xl font-black text-xs uppercase tracking-widest transition-all mt-2"
            >
              {t('tesla.close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedRoutePage;
